import { apiService } from './api-service';
import { authService } from './auth-service';
import { demoDataService } from './demo-data-service';
import { DuplicateWorkOrderError } from './errors';


import type {
	WorkOrder,
	Association,
	AssociationShortcutCode,
	User,
	TimeEntry,
	Material,
	MaterialCost,
	MiscCost,
	Note,
	Attachment,
	LocationEvent,
	InspectionReport,
	InspectionIssue,
	InspectionReportIssue,
	LocationAnalyticsEntry,
	FeatureFlag,
	Task,
	FollowUp,
	CalendarSource,
	CalendarEvent,
	ExteriorApplicationWithMeta,
	ApplicationRequestType,
	ApplicationRuleset,
} from '@/types/models';
import type {
	WorkOrdersReportParams,
	WorkOrdersReportResponse,
} from '@/types/reports';
import { ApiResponseError } from '@/utils/api-error';
import type { ApplicationStage } from '@/utils/application-stage';
import { exteriorImprovementsReportQuery } from '@/utils/exterior-improvements-report';
import type { MessageStatus } from '@/utils/inbox-triage';
import { logger } from '@/utils/logger';
import type { DeliveryStatus } from '@/utils/message-delivery';
import type { RuleAction } from '@/utils/message-rules';
import { appendNativeFile, type NativeFileSource } from '@/utils/native-form-file';
import { openFetchedFile, openFetchedPdf } from '@/utils/open-fetched-pdf';
// Type-only, so nothing from the Workers-side module is bundled into the client. Shared
// rather than re-declared: this union had already been hand-copied twice below, and both
// copies went stale the moment a fifth gate was added.
import type { EmailGate } from '@/utils/server-email';


export { DuplicateWorkOrderError };

interface Dimensions {
	width: number;
	height: number;
}

const parsePositiveNumber = (value: unknown): number | null => {
	if (value === null || value === undefined) {
		return null;
	}
	const numeric = typeof value === 'number' ? value : Number(value);
	if (!Number.isFinite(numeric) || numeric <= 0) {
		return null;
	}
	return numeric;
};

const parseImageSizeDescriptor = (value: unknown): Dimensions | null => {
	if (!value) {
		return null;
	}
	if (typeof value === 'string') {
		const trimmed = value.trim();
		if (!trimmed) {
			return null;
		}
		if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
			try {
				const parsed = JSON.parse(trimmed);
				return parseImageSizeDescriptor(parsed);
			} catch (error) {
				// ignore malformed JSON
			}
		}
		const match = trimmed.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
		if (match) {
			const width = parsePositiveNumber(match[1]);
			const height = parsePositiveNumber(match[2]);
			if (width && height) {
				return { width, height };
			}
		}
		const parts = trimmed.split(/[ ,]/).filter(Boolean);
		if (parts.length >= 2) {
			const width = parsePositiveNumber(parts[0]);
			const height = parsePositiveNumber(parts[1]);
			if (width && height) {
				return { width, height };
			}
		}
	}
	if (Array.isArray(value)) {
		if (value.length >= 2) {
			const width = parsePositiveNumber(value[0]);
			const height = parsePositiveNumber(value[1]);
			if (width && height) {
				return { width, height };
			}
		}
	}
	if (typeof value === 'object') {
		const raw = value as Record<string, unknown>;
		const width = parsePositiveNumber(raw.width ?? raw.w);
		const height = parsePositiveNumber(raw.height ?? raw.h);
		if (width && height) {
			return { width, height };
		}
	}
	return null;
};

const pickDimension = (values: unknown[]): number | undefined => {
	for (const candidate of values) {
		const numeric = parsePositiveNumber(candidate);
		if (numeric) {
			return numeric;
		}
	}
	return undefined;
};

const resolveImageDimensions = (source: any): { width?: number; height?: number; size?: Dimensions } => {
	if (!source) {
		return {};
	}
	const parsedSize = parseImageSizeDescriptor(source.imageSize ?? source.image_size ?? source.dimensions ?? null);
	const width = pickDimension([source.imageWidth, source.image_width, source.width, parsedSize?.width, source.meta?.width]);
	const height = pickDimension([source.imageHeight, source.image_height, source.height, parsedSize?.height, source.meta?.height]);
	const normalizedSize = width && height ? { width, height } : parsedSize ?? undefined;
	return {
		width,
		height,
		size: normalizedSize,
	};
};

// Map API eventType to LocationEvent.type for consumers that use type/LocationEventType
const normalizeLocationEventFromApi = (e: Record<string, unknown>): Record<string, unknown> => {
	const et = e.eventType as string | undefined;
	const type = et === 'checkout' ? 'check_out' : et === 'checkin' ? 'check_in' : (e.type as string) ?? et;
	const ulat = e.userLatitude ?? e.lat ?? (e as Record<string, unknown>).latitude;
	const ulng = e.userLongitude ?? e.lng ?? (e as Record<string, unknown>).longitude;
	const rawTs = e.eventTimestamp ?? (e.timestamp ? new Date(String(e.timestamp)).getTime() : null);
	const ts = typeof rawTs === 'number' && Number.isFinite(rawTs) ? rawTs : null;
	const tsIso = ts != null ? new Date(ts).toISOString() : ((e.timestamp ?? e.createdAt) as string) ?? '';
	return {
		...e,
		type,
		eventType: et ?? type,
		lat: ulat,
		lng: ulng,
		userLatitude: ulat,
		userLongitude: ulng,
		timestamp: tsIso,
		eventTimestamp: ts ?? undefined,
	};
};

// Internal helper: normalize attachment fields from API and compute derived flags
const normalizeAttachment = (a: any): Attachment => {
	const fileName = a.fileName || a.name || '';
	const mimeType = a.mimeType || a.contentType || '';
	const fileType = (a.fileType || (fileName.split('.').pop() || '')).toLowerCase();
	const size = a.fileSize ?? a.size ?? 0;
	const storageKey = a.storageKey || a.key || a.uri || '';
	const attachmentType = a.attachmentType || a.type;
	const inspectionReportId = a.inspectionReportId || a.inspection_report_id;
	const photoTakenAtMs = a.photoTakenAt
		? (typeof a.photoTakenAt === 'number' ? a.photoTakenAt : Date.parse(a.photoTakenAt))
		: (a.photo_taken_at ?? null);
	const photoTakenAt = photoTakenAtMs ? new Date(photoTakenAtMs).toISOString() : undefined;
	const photoLat = a.photoLat ?? a.photo_lat;
	const photoLng = a.photoLng ?? a.photo_lng;

	const { width: resolvedWidth, height: resolvedHeight, size: resolvedSize } = resolveImageDimensions(a);
	const dimensionProps: Partial<Attachment> = {};
	if (resolvedWidth !== undefined) {
		dimensionProps.width = resolvedWidth;
		dimensionProps.imageWidth = resolvedWidth;
	}
	if (resolvedHeight !== undefined) {
		dimensionProps.height = resolvedHeight;
		dimensionProps.imageHeight = resolvedHeight;
	}
	if (resolvedSize) {
		dimensionProps.imageSize = resolvedSize;
	}

	const inferredMime = (() => {
		// If we have a valid MIME type, use it
		if (mimeType && mimeType !== 'application/octet-stream') {
			return mimeType;
		}

		// Otherwise, infer from file extension
		switch (fileType) {
			case 'jpg':
			case 'jpeg': return 'image/jpeg';
			case 'png': return 'image/png';
			case 'gif': return 'image/gif';
			case 'webp': return 'image/webp';
			case 'bmp': return 'image/bmp';
			case 'heic': return 'image/heic';
			case 'heif': return 'image/heif';
			case 'image': return 'image/jpeg'; // Handle generic 'image' type
			case 'mp4': return 'video/mp4';
			case 'webm': return 'video/webm';
			case 'mov': return 'video/quicktime';
			case 'avi': return 'video/x-msvideo';
			case 'mkv': return 'video/x-matroska';
			case 'm4v': return 'video/x-m4v';
			case 'pdf': return 'application/pdf';
			default: return 'application/octet-stream';
		}
	})();

	const isImage = (inferredMime || '').startsWith('image/');
	const isVideo = (inferredMime || '').startsWith('video/');
	const isPdf = (inferredMime || '') === 'application/pdf' || fileType === 'pdf';

	return {
		...a,
		fileName,
		fileType,
		fileSize: size,
		mimeType: inferredMime,
		storageKey,
		attachmentType,
		inspectionReportId,
		photoTakenAt,
		photoLat,
		photoLng,
		isImage,
		isVideo,
		isPdf,
		name: fileName,
		size,
		type: attachmentType,
		uri: a.uri,
		...dimensionProps,
	} as Attachment;
};

export interface PaginatedResponse<T> {
	data: T[];
	total: number;
	page: number;
	limit: number;
	hasNext: boolean;
	hasPrev: boolean;
}

export interface QueryOptions {
	page?: number;
	limit?: number;
	sortBy?: string;
	sortOrder?: 'asc' | 'desc';
	[key: string]: any;
}

export interface ExteriorApplicationQuery {
	associationId?: string;
	accountId?: string;
	/** Comma-separated integer statuses (e.g. "0,1,2"). */
	status?: string;
	unmatched?: boolean;
	search?: string;
	page?: number;
	limit?: number;
}

export interface ExteriorApplicationListResponse {
	data: ExteriorApplicationWithMeta[];
	pagination: { page: number; limit: number; total: number; pages: number };
}

export interface ApplicationCorrespondencePayload {
	toAddress: string;
	ccAddress?: string;
	subject: string;
	bodyText?: string;
	bodyHtml?: string;
}


/** Another live request on the same lot — a merge candidate, not a link target. */
export interface SiblingRequest {
	id: string;
	applicationNumber: number | null;
	requestTypeName: string | null;
	description: string | null;
	statusLabel: string;
}

/** One of a request type's requirements — the cheat-sheet a reviewer works through. */
export interface ApplicationChecklistItem {
	key: string;
	label: string;
	required: boolean;
	/** True when a FILE answers it, as opposed to someone ticking the box. */
	satisfiedByFile: boolean;
	checked: boolean;
}

/** An improvement request plus the messages linked to it. */
export interface ExteriorApplicationDetail {
	id: string;
	applicationNumber?: number;
	associationId: string;
	associationName?: string;
	/** The association's property manager — `associations.assigned_to`. */
	managerId?: string | null;
	managerName?: string;
	/** Their persisted chart colour, so a PM reads the same here as in the Inbox. */
	managerColor?: string;
	accountId: string | null;
	accountNumber?: string;
	accountOwnerName?: string;
	accountAddress?: string;
	requestTypeId: string;
	requestTypeName?: string;
	status: number;
	statusLabel: string;
	label?: string;
	submitterName?: string;
	submitterEmail?: string;
	submitterPhone?: string;
	propertyAddress?: string;
	description?: string;
	fields: Record<string, unknown>;
	checklistState: Record<string, boolean>;
	reviewDeadline: number | null;
	decisionNotes?: string;
	boardMeetingDate: number | null;
	decidedBy: string | null;
	/** The approver's name, joined server-side; null when undecided (#611). */
	decidedByName: string | null;
	decidedAt: number | null;
	createdAt?: string;
	messageCount: number;
	unreadCount: number;
	/** Derived: where this sits in the workflow, including "the owner replied, back to us". */
	stage: ApplicationStage;
	/** Other OPEN requests on this lot, offered for merge. Detail response only. */
	siblingRequests?: SiblingRequest[];
	/** Every file across every linked message. Detail response only. */
	attachments?: MessageAttachment[];
	/** The request type's requirements, each answered or not. Detail response only. */
	checklist?: ApplicationChecklistItem[];
	checklistComplete?: boolean;
	messages?: Array<{
		id: string;
		subject?: string;
		bodyText?: string;
		fromName?: string;
		fromAddress?: string;
		direction: number;
		relation: string;
		toAddress?: string;
		deliveryStatus?: DeliveryStatus | null;
		deliveryNote?: string | null;
		readAt: string | null;
		createdAt?: string;
	}>;
}

export type ReportDateField = 'submitted' | 'decided';

export interface ExteriorImprovementsReportParams {
	/** YYYY-MM-DD, ET business day. */
	from: string;
	to: string;
	dateField: ReportDateField;
	associationId?: string;
	statuses?: number[];
	detail?: boolean;
}

export interface ExteriorImprovementsReportAssociation {
	id: string;
	name: string;
	counts: { total: number; byStatus: Record<string, number>; byType: Record<string, number> };
	items: ExteriorApplicationDetail[];
}

export interface ExteriorImprovementsReport {
	from: string;
	to: string;
	dateField: ReportDateField;
	associations: ExteriorImprovementsReportAssociation[];
}

/** A message in the unified inbox (#369). */
export interface InboxLabel {
	id: string;
	name: string;
	slug: string;
	color: string | null;
	icon: string | null;
	isSystem: boolean;
	/** Present on the label registry, not on a message's own labels. */
	total?: number;
	unread?: number;
}

/**
 * The label registry plus the size of the triage queue.
 *
 * `needsHuman` is counted DISTINCT server-side rather than summed from `total` here,
 * because a message can wear both triage labels and the drawer badge, the rail section and
 * the `?label=needs-human` list all have to show the same number.
 */
/** One editable letter. `requestTypeId === null` is the default for its kind. */
/** A canned inbox reply (`inbox_reply_templates`). Message-scoped tags only. */
export interface InboxReplyTemplate {
	id: string;
	name: string;
	subject: string;
	body: string;
	sortOrder: number;
	enabled: boolean;
	createdAt: number | null;
	updatedAt: number | null;
}

export interface EmailTemplate {
	id: string;
	kind: string;
	requestTypeId: string | null;
	requestTypeName: string | null;
	name: string;
	subject: string;
	body: string;
	enabled: boolean;
	notes: string | null;
	createdAt: number | null;
	updatedAt: number | null;
}

/**
 * The composed "needs additional info" letter, before anyone sends it.
 *
 * `emailGate` is here rather than only on the send result so the composer can say "this
 * will be recorded but not emailed, because X" while the reviewer is still writing.
 */
export interface NeedsInfoEmailPreview {
	applicationId: string;
	canSend: boolean;
	/** Things that make the send IMPOSSIBLE. In practice only "no submitter email". */
	blockers: string[];
	/**
	 * Context that used to be blockers — nothing outstanding, no template, an illegal status
	 * transition. Informational: the send goes ahead, the reviewer just needs to know what
	 * they are sending. Rendered muted, not as errors.
	 */
	notes: string[];
	to: string | null;
	from: { email: string; name: string };
	replyTo: string;
	subject: string;
	bodyText: string;
	missing: Array<{ key: string; label: string }>;
	missingKeys: string[];
	checklistComplete: boolean;
	templateId: string | null;
	/** `'builtin'` when the wording came from `FALLBACK_FOLLOW_UP_TEMPLATE`, not a row. */
	templateSource: 'request_type' | 'default' | 'builtin' | null;
	templateWarnings: string[];
	alreadySentAt: number | null;
	/** Whether that earlier letter reached the homeowner — see `utils/message-delivery.ts`. */
	alreadySentDelivery?: PreviousLetterDelivery | null;
	/**
	 * `willChange` is whether the move is OFFERED and pre-ticked (something outstanding, and
	 * legal); `allowed` is whether it is legal at all. They differ on a complete request,
	 * where the composer must not tick "needs more information" by default.
	 */
	statusChange: { from: number; to: number; willChange: boolean; allowed: boolean };
	emailGate: {
		blockedBy: EmailGate;
		environment: string | null;
		orgSendingEnabled: boolean;
		bindingConfigured: boolean;
		willActuallySend: boolean;
		reason: string | null;
	};
	/** The full attachment set (#606): the request's files, uploads, Dropbox. */
	attachable?: ComposerAttachable;
}

export interface NeedsInfoSendBody {
	subject?: string;
	bodyText?: string;
	/** Refuses the send if the checklist moved since the preview was built. */
	expectedMissingKeys?: string[];
	updateStatus?: boolean;
	force?: boolean;
	/** Files attached to the letter (#606). */
	attachments?: ReplyAttachmentRef[];
}

export interface NeedsInfoSendResult {
	messageId: string | null;
	rfcMessageId: string | null;
	statusChanged: boolean;
	status: number;
	emailResult: {
		sent: boolean;
		skipped: boolean;
		gate: 'environment' | 'organization' | 'binding' | 'send_error' | null;
		error: string | null;
	};
}

/**
 * The composed "Waiting for Board Approval" letter (#555). Same envelope as the needs-info
 * preview, plus the attachment the homeowner will receive — named before anyone sends it.
 */
/** The delivery state of the last letter of a kind, for the composer's "already wrote" note. */
export interface PreviousLetterDelivery {
	status: DeliveryStatus | null;
	note: string | null;
	to: string | null;
}

export interface BoardApprovalEmailPreview {
	applicationId: string;
	canSend: boolean;
	blockers: string[];
	notes: string[];
	to: string | null;
	from: { email: string; name: string };
	replyTo: string;
	subject: string;
	bodyText: string;
	templateId: string | null;
	templateSource: 'request_type' | 'default' | null;
	alreadySentAt: number | null;
	/** Whether that earlier letter reached the homeowner — see `utils/message-delivery.ts`. */
	alreadySentDelivery?: PreviousLetterDelivery | null;
	/** The PDF's name. The composer stages the file itself via `stageBoardApprovalAttachment`. */
	attachment: { fileName: string };
	/**
	 * The request's files the packet cannot print (PDFs, Word files, …), offered as separate
	 * attachments beside it (#608). The composer ticks them all by default.
	 */
	documents?: BoardApprovalDocument[];
	/** Budget for the packet PDF plus every ticked document. */
	maxAttachmentBytes?: number;
	/** Uploads and Dropbox picks the PM can ADD (#606) — counted in the same budget. */
	attachable?: ComposerAttachable;
	/** The request state this text was composed from — sent back with Send. */
	version: BoardApprovalVersion;
	statusChange: { from: number; to: number; willChange: boolean; allowed: boolean };
	emailGate: NeedsInfoEmailPreview['emailGate'];
}

/** A request file that rides beside the packet PDF rather than being printed in it. */
export interface BoardApprovalDocument {
	id: string;
	fileName: string;
	mimeType: string | null;
	fileSize: number | null;
}

/** Which moment of the request a letter's text or its PDF describes. */
export interface BoardApprovalVersion {
	status: number;
	updatedAt: number | null;
	/** Revision of externally stored values printed in the packet. */
	contentVersion: string;
}

/** A rendered application waiting in storage for the PM to press Send. */
export interface StagedBoardApprovalAttachment {
	token: string;
	fileName: string;
	sizeBytes: number;
	/** The request state the PDF was rendered from. */
	version: BoardApprovalVersion;
}

/** Everything is the composer's: the server never fills in a subject, body or PDF itself. */
export interface BoardApprovalSendBody {
	subject: string;
	bodyText: string;
	attachmentToken: string;
	/** The preview's `version`; the send refuses unless the staged PDF matches it. */
	expectedVersion: BoardApprovalVersion;
	updateStatus?: boolean;
	force?: boolean;
	/** The request documents left ticked, attached beside the packet (#608). */
	documentIds?: string[];
	/** Uploads and Dropbox picks the PM added (#606). */
	attachments?: ReplyAttachmentRef[];
}

export interface BoardApprovalSendResult {
	messageId: string | null;
	rfcMessageId: string | null;
	statusChanged: boolean;
	status: number;
	/** Null when nothing went out — the send failed. */
	attachment: { fileName: string; sizeBytes: number } | null;
	/** The documents that went beside it. */
	documents?: Array<{ fileName: string; sizeBytes: number }>;
	emailResult: {
		sent: boolean;
		skipped: boolean;
		gate: 'environment' | 'organization' | 'binding' | 'send_error' | null;
		error: string | null;
		/** The preview sink's mailbox, when it took the letter instead of the homeowner. */
		sunkTo?: string | null;
	};
}

export interface LabelRegistry {
	labels: InboxLabel[];
	needsHuman: number;
	/** What is on the CALLER's desk, filtered exactly like every other rail badge. */
	assignedToMe: number;
	/**
	 * How much mail is in each PLACE — Archived (#383), Waiting and Spam (migration 261).
	 *
	 * Scoped like the others but NOT filtered by `unfiledOnly` — those views ignore that
	 * filter too, and a rail row whose count disagrees with the list it opens is the defect
	 * every other badge here is written to avoid.
	 */
	statuses: Record<Exclude<MessageStatus, 'open'>, number>;
	/** @deprecated Use `statuses.archived`. */
	archived: number;
}

export interface MessageLink {
	id: string;
	objectType: string;
	objectId: string;
	relation: 'origin' | 'related';
}

/** A link resolved to something renderable; `missing` when the target is gone. */
export interface LinkedObject {
	objectType: string;
	objectId: string;
	title: string;
	subtitle?: string;
	status?: string;
	href?: string;
	missing?: boolean;
}

export interface InboxMessage {
	id: string;
	threadId: string;
	kind: 'email' | 'form_submission' | 'note';
	direction: number;
	subject?: string;
	bodyText?: string;
	bodyHtml?: string;
	fromAddress?: string;
	fromName?: string;
	/** Who we wrote to, on an outbound row. The API has always sent it. */
	toAddress?: string;
	formType?: string;
	/** Did a letter we recorded actually go out? See `utils/message-delivery.ts`. */
	deliveryStatus?: DeliveryStatus | null;
	deliveryNote?: string | null;
	fields: Record<string, string>;
	associationNameRaw?: string;
	submitterName?: string;
	submitterEmail?: string;
	submitterPhone?: string;
	propertyAddress?: string;
	associationId: string | null;
	associationName?: string;
	/** The association's property manager — `associations.assigned_to`. */
	managerId?: string | null;
	managerName?: string;
	/** Their persisted chart colour (`users.color`), so a PM reads the same everywhere. */
	managerColor?: string;
	/**
	 * Who is DEALING with this message — a different question from whose association it
	 * is. Null is the normal state; the field earns its keep by going back to null once
	 * someone has handled it.
	 */
	assignedTo?: string | null;
	assignedToName?: string;
	assignedToColor?: string;
	accountId: string | null;
	accountNumber?: string;
	accountOwnerName?: string;
	matchSource: string | null;
	readAt: string | null;
	/**
	 * Where this message lives — open work, parked on the homeowner, junk, or filed away.
	 * Exactly one at a time (migration 261).
	 */
	status: MessageStatus;
	/** When it entered that state, and who put it there (#534). */
	statusAt: string | null;
	statusBy: string | null;
	statusByName?: string;
	/**
	 * When this was put away. Filter on `status`, not on this — but it is a permanent
	 * second record of the archive instant, kept in step by the server.
	 */
	archivedAt: string | null;
	/** When a complaint was put in front of the board (#566); null when it has not been. */
	reportedToBoardAt?: string | null;
	createdAt?: string;
	labels: InboxLabel[];
	links: MessageLink[];
	/**
	 * The latest time an inbox RULE tried to answer this message by itself — list and
	 * detail payloads only; null when no rule ever did. See `InboxAutoReply`.
	 */
	autoReply?: InboxAutoReply | null;
}

/**
 * One rule-sent reply attempt (api/message-auto-reply.ts `AutoReplyInfo`).
 *
 * `outcome` is the honest version of "sent": only `sent` reached the homeowner. `sunk`
 * went to the preview test mailbox instead, `held` was stopped by a send gate and exists
 * only as the recorded copy, `failed` errored, `skipped` never wrote anything.
 */
export interface InboxAutoReply {
	outcome: 'sent' | 'sunk' | 'held' | 'failed' | 'skipped';
	ruleName: string | null;
	templateId: string | null;
	templateName: string | null;
	to: string[];
	sunkTo: string | null;
	reason: string | null;
	replyMessageId: string | null;
	at: string | null;
	/** The history note, verbatim. */
	summary: string;
	/** What went out — detail payload only. */
	reply?: { id: string; subject: string | null; bodyText: string | null } | null;
	/**
	 * Detail only: an older (pre-structured) reply whose letter could not be matched to it
	 * unambiguously, so no text is shown rather than possibly the wrong one.
	 */
	detailsUnavailable?: boolean;
}

/** One association's node in the grouped rail — GET /messages/groups. */
export interface InboxGroup {
	associationId: string;
	associationName: string;
	managerId: string | null;
	managerName: string | null;
	total: number;
	unread: number;
	needsHuman: number;
	labels: Array<{ slug: string; total: number; unread: number }>;
}

export interface InboxGroups {
	/** Mail matching no association. Always empty for a property manager, by design. */
	unassociated: { total: number; unread: number; needsHuman: number };
	groups: InboxGroup[];
}

/** GET /messages/:id — the message plus its conversation and what it became. */
/** A file we hold for a message — bytes in R2, served through one URL. */
export interface MessageAttachment {
	id: string;
	fileName: string;
	fileType: string;
	mimeType: string | null;
	fileSize: number | null;
	/** The link it was fetched from, when it came from a website form rather than a person. */
	sourceUrl: string | null;
	attachmentType: string;
	/** What the form called the field — "Literature 2". */
	label: string | null;
	/** Which of the request type's requirements this file answers, if any. */
	checklistKey?: string | null;
	createdAt?: string;
}

/** `POST /exterior-applications` — a request started by staff rather than from a message. */
export interface NewApplicationBody {
	associationId: string;
	accountId?: string;
	requestTypeId: string;
	description?: string;
	submitterName?: string;
	submitterEmail?: string;
	submitterPhone?: string;
	propertyAddress?: string;
}

/** What the client sends after the bytes are already in R2. */
export interface ApplicationAttachmentUpload {
	storageKey: string;
	fileName: string;
	mimeType?: string;
	fileSize?: number;
	attachmentType?: string;
	/** The requirement this file answers. Refused by the server if the type doesn't declare it. */
	checklistKey?: string | null;
	description?: string | null;
}

/**
 * Deliberately NOT `MessageAttachment`: that shape carries `label` and `sourceUrl`, which
 * describe where a FETCHED file came from and what the form called it. Neither exists for a
 * row a member of staff created a millisecond ago, so claiming them would be inventing state.
 */
export interface AddedApplicationAttachment {
	id: string;
	fileName: string;
	fileType: string;
	mimeType: string | null;
	fileSize: number | null;
	attachmentType: string;
	checklistKey?: string | null;
	description?: string | null;
	createdAt?: string;
	/** The note message it was anchored to. */
	messageId: string;
}

export interface RelatedRequest {
	id: string;
	applicationNumber: number | null;
	requestTypeName: string | null;
	description: string | null;
	statusLabel: string;
}

export interface InboxMessageDetail extends InboxMessage {
	linkedObjects: LinkedObject[];
	attachments: MessageAttachment[];
	/** Upload links in the body we do NOT hold yet — fetchable, not lost. */
	pendingUploadLinks: string[];
	thread: InboxMessage[];
	/** Other OPEN requests on the same lot — "is this one of these?" */
	relatedRequests: RelatedRequest[];
	/** Every rule auto-reply attempt, newest first, each with the text that went out. */
	autoReplies?: InboxAutoReply[];
}

/** GET /messages/:id/forward — everything the composer needs to be honest up front. */
export interface ForwardPreview {
	subject: string;
	bodyText: string;
	from: { email: string; name: string };
	replyTo: string | null;
	emailGate: {
		blockedBy: EmailGate;
		reason: string | null;
		willActuallySend: boolean;
	};
	attachments: Array<{ id: string; fileName: string; fileSize: number | null }>;
	/** Human-readable Dropbox destination, or null when Dropbox is not connected. */
	dropboxTarget: string | null;
	alreadySaved: boolean;
	/** Files the sender can ADD as real attachments — uploads, Dropbox (#606). No `files` list. */
	attachable?: ComposerAttachable;
	/**
	 * The forwarder's closing block, for seeding the note box.
	 *
	 * The composer MUST render it into the editable note rather than leaving it to the
	 * server — otherwise the recipient gets text the sender never saw, and a sender who
	 * types their own closing ends up with two signatures and no way to delete the
	 * automatic one. Nothing appends it server-side.
	 */
	signature: string;
}

export interface SavedToDropbox {
	attachmentId: string;
	fileName: string;
	path: string;
	/** Dropbox web-app viewer URL — opens only for someone with access to the drive. */
	link: string;
	reused: boolean;
}

export interface SaveToDropboxResult {
	saved: SavedToDropbox[];
	skipped: Array<{ attachmentId: string; fileName: string; reason: string }>;
	target: string;
}

export interface ForwardResult {
	sent: boolean;
	gate: 'suppressed' | 'environment' | 'organization' | 'binding' | null;
	reason: string | null;
	/** The recorded outbound message, on the original's thread. */
	messageId: string;
	dropbox: SaveToDropboxResult | null;
}

/** One person it would be reasonable to answer — GET /messages/:id/reply. */
export interface ReplyRecipient {
	email: string;
	name: string;
	/**
	 * `submitter` filled in the form; `owner` owns the lot it matched; `sender` emailed us
	 * directly and this is the address they wrote from.
	 */
	source: 'submitter' | 'owner' | 'sender';
}

export interface ReplyTemplateSummary {
	id: string;
	name: string;
	subject: string;
	bodyText: string;
	/** `reply` = canned inbox reply; `letter` = an improvement-letter default. */
	group: 'reply' | 'letter';
	/**
	 * Tags this template uses that a plain message cannot resolve — they render BLANK.
	 * Non-empty on most `letter` entries, since those were written for an application.
	 */
	unresolvedTags: string[];
}

/** GET /messages/:id/reply — everything the composer needs to be honest up front. */
export interface ReplyPreview {
	to: string | null;
	recipients: ReplyRecipient[];
	subject: string;
	bodyText: string;
	from: { email: string; name: string };
	/** Plus-addressed, so the homeowner's answer comes back onto this message. */
	replyTo: string;
	emailGate: {
		blockedBy: EmailGate;
		reason: string | null;
		willActuallySend: boolean;
	};
	/** Things that make a send impossible. Reply currently has none. */
	blockers: string[];
	/**
	 * Context that does NOT block — e.g. no address was discovered, so type one. Rendered
	 * muted. This used to be a blocker, which disabled Send even after a PM typed a valid
	 * address: an unfixable-from-the-UI gate on an editable field.
	 */
	notes: string[];
	canSend: boolean;
	templates: ReplyTemplateSummary[];
	ccSelf: boolean;
	/**
	 * The replier's own signature, '' when unset — seeded into the editable message box
	 * (#564). Nothing appends it server-side, so the box is the only copy that goes out.
	 */
	signature: string;
	/** What can be attached, from where, and the total-size budget (#603, #606). */
	attachable: ComposerAttachable;
	/**
	 * The conversation the send quotes under the message (#712) — the exact text, so the
	 * composer shows what goes out. `text` is '' when there is nothing to quote.
	 */
	history: { text: string; count: number; omitted: number };
}

/**
 * What a composer can attach — the same shape from every composer's preview (#606): Reply,
 * Forward, the Needs-info letter and the Board Approval letter.
 */
export interface ComposerAttachable {
	/** Files already in reach — the conversation's, or the request's. Empty where none are offered. */
	files: ReplyAttachableFile[];
	/** Linked requests whose packet PDF can be attached (conversations only). */
	applications: Array<{ id: string; label: string }>;
	maxBytes: number;
	/** Whether to offer the Dropbox picker, and the association folder it opens in. */
	dropbox: { connected: boolean; startPath: string | null };
}

/** What a composer writes about — which staging routes its files go through (#606). */
export type ComposeTarget = { kind: 'message'; id: string } | { kind: 'application'; id: string };

const composeAttachmentsBase = (target: ComposeTarget): string => (target.kind === 'message'
	? `/messages/${target.id}/reply/attachments`
	: `/exterior-applications/${target.id}/compose-attachments`);

/** A file already on the conversation or its linked request, attachable by reference (#603). */
export interface ReplyAttachableFile {
	id: string;
	fileName: string;
	mimeType: string | null;
	fileSize: number | null;
	origin: 'thread' | 'request';
}

/** New bytes staged for a composer — only the person who staged them can open or send them. */
export interface StagedReplyAttachment {
	token: string;
	fileName: string;
	mimeType: string;
	sizeBytes: number;
}

/** What an email carries per file: a staged upload, or a file already on the conversation/request. */
export type ReplyAttachmentRef = { staged: string } | { existing: string };

export interface ReplyResult {
	sent: boolean;
	gate: 'suppressed' | 'environment' | 'organization' | 'binding' | null;
	reason: string | null;
	messageId: string;
	/** What the original was moved to, or null when it was left where it was. */
	movedTo: string | null;
}

/** One inbound-routing rule — GET /message-rules. */
export interface MessageRuleRecord {
	id: string;
	name: string;
	sortOrder: number;
	enabled: boolean;
	conditions: Array<{ field: string; op: string; value: string }>;
	/** What the rule DOES, applied in order. See `utils/message-rules.ts`. */
	actions: RuleAction[];
	/** Matches every message, conditions ignored — the catch-all at the bottom. */
	matchAll: boolean;
	createdAt?: string;
	updatedAt?: string;
}

/** One chunk of a rule backfill — POST /message-rules/:id/run. */
export interface RuleRunChunk {
	ruleId: string;
	scanned: number;
	matched: number;
	/** Zero on a dry run: matches are counted, nothing is written. */
	applied: number;
	/**
	 * Letters this run deliberately did NOT send.
	 *
	 * A backfill never mails anybody — it would turn one button press into a mass-send to
	 * every homeowner still in the open inbox. Reported rather than hidden so the modal can
	 * say what it skipped, instead of quietly doing less than the rule appears to say.
	 */
	repliesSuppressed: number;
	/** Feed back into the next call. Null when there is nothing left to scan. */
	cursor: string | null;
	done: boolean;
}

export interface MessageQuery {
	label?: string;
	accountId?: string;
	associationId?: string;
	q?: string;
	unread?: boolean;
	unmatched?: boolean;
	/** @deprecated Send `status: 'archived'`. Still honoured by the server. */
	archived?: boolean;
	/**
	 * Which PLACE to show — only mail in that state, never "also include it". Omit for the
	 * working inbox.
	 */
	status?: Exclude<MessageStatus, 'open'>;
	/** A user id, `me` for the caller, or `none` for what nobody has picked up. */
	assignedTo?: string;
	/** Only messages no request has been opened from yet (and not reviewed/dismissed). */
	withoutRequest?: boolean;
	/**
	 * Only messages nothing has been done with — no link of any kind, and not reviewed
	 * or dismissed. The type-agnostic form of `withoutRequest`, and the inbox default.
	 */
	unfiled?: boolean;
	/** Account RECORD mode (#613): every non-spam state, archived last. Needs `accountId`. */
	history?: boolean;
	/**
	 * With `q`: search every place but Spam, filed mail included (#716), instead of only the
	 * current place. Ignored without a search.
	 */
	everywhere?: boolean;
	limit?: number;
	page?: number;
}

/** A page of the inbox list. */
export interface MessageListPage {
	data: InboxMessage[];
	pagination: { total: number; page: number; pages: number };
	/** On a search that was not widened: matches outside the current place (Spam excluded). */
	elsewhere?: number;
	/** The server searched every place but Spam. */
	everywhere?: boolean;
}

export interface ApplicationIntakeItem {
	id: string;
	fromAddress?: string;
	fromName?: string;
	subject?: string;
	bodyText?: string;
	bodyHtml?: string;
	status: 'new' | 'converted' | 'dismissed';
	createdApplicationId?: string | null;
	createdAt?: string;
}

export interface FormSubmissionItem {
	id: string;
	formType: string;
	subject?: string;
	bodyText?: string;
	bodyHtml?: string;
	submitterName?: string;
	submitterEmail?: string;
	submitterPhone?: string;
	propertyAddress?: string;
	associationNameRaw?: string;
	fields: Record<string, string>;
	attachments: unknown[];
	associationId: string | null;
	associationName?: string;
	accountId: string | null;
	accountNumber?: string;
	accountOwnerName?: string;
	matchSource: string | null;
	status: 'new' | 'reviewed' | 'dismissed';
	readAt: string | null;
	reviewedAt: string | null;
	createdAt?: string;
}

export interface FormSubmissionCounts {
	byType: Record<string, { total: number; open: number; unread: number }>;
	open: number;
	unread: number;
}

export interface ReclassifyApplicationResult {
	submissionId: string;
	formType: string;
	/** When the soft-deleted application stops being recoverable. */
	purgeBy: number;
	attachmentsMoved: number;
}

export interface PlannedContact {
	action: 'create' | 'update';
	personId?: string;
	firstName: string;
	lastName: string | null;
	email: string | null;
	phone: string | null;
	role: 'owner' | 'emergency_contact';
	/** Only meaningful for an emergency contact — 'Son', 'Neighbour', … */
	relationship: string | null;
	matchedBy?: 'email' | 'name';
	/** Emergency contact currently linked as an owner — applying retires that owner link. */
	retiresOwnerLink?: boolean;
	/** Emergency contact who is also a new owner on this form — one person, two links. */
	sameAsOwner?: boolean;
}

export interface ContactPlan {
	submissionId: string;
	accountId: string | null;
	accountNumber: string | null;
	people: PlannedContact[];
	/** Things a human should look at before applying. */
	warnings: string[];
}

export interface ApplyContactsResult {
	created: number;
	updated: number;
	linked: number;
	warnings: string[];
}

export interface IntakeMatchCandidate {
	shortcode: string;
	accountId: string;
	accountNumber: string;
	associationId: string;
	associationName: string | null;
	ownerName: string | null;
	address: string | null;
}

export interface IntakeOpenRequest {
	id: string;
	applicationNumber: number | null;
	requestTypeName: string | null;
	description: string | null;
	status: number;
	statusLabel: string;
	createdAt?: string;
}

export interface IntakeMatchContext {
	/** Shortcode tokens found in the subject. */
	shortcodes: string[];
	account: {
		id: string;
		accountNumber: string;
		ownerName: string | null;
		address: string | null;
		associationId: string;
		associationName: string | null;
	} | null;
	/** Populated instead of `account` when a token hit several lots. */
	ambiguous: IntakeMatchCandidate[];
	openRequests: IntakeOpenRequest[];
	/** 'attach' when exactly one request is open, 'ask' when several, else 'create'. */
	recommendation: 'attach' | 'ask' | 'create';
}

export interface ReclassifyIntakeResult {
	submissionId: string;
	formType: string;
	accountId: string | null;
	associationId: string | null;
	/** False when this email had already been recorded (same Message-ID). */
	created: boolean;
}

export interface MergeApplicationResult {
	survivorId: string;
	mergedId: string;
	correspondenceMoved: number;
	/** Messages the survivor already held, so they weren't duplicated. */
	correspondenceSkipped: number;
	attachmentsMoved: number;
}

export interface ApplicationIntakeConvertPayload {
	associationId: string;
	requestTypeId: string;
	accountId?: string;
}

/**
 * Counts behind the "needs attention" badge. `total` is `intake + unread` — see the
 * note on `attentionCounts` in api/exterior-applications.ts for why pendingUs is
 * reported but deliberately not badged.
 */
export interface ApplicationAttentionCounts {
	intake: number;
	unread: number;
	pendingUs: number;
	total: number;
}

/**
 * What the Exterior Improvements section still owes a person.
 *
 * `unconverted` is the one that bites: an improvement message is invisible to this section
 * until someone opens a request from it, so a queue of real submissions can sit in the
 * inbox while this page reports nothing to do. `needsHuman` is the sum the rail badges.
 */
export interface ApplicationCounts {
	pendingUs: number;
	byStage: Record<string, number>;
	unconverted: number;
	needsHuman: number;
}

/** One approver on the Approved rail group (#611). `name` is null for a purged user. */
export interface ApplicationApprover {
	decidedBy: string;
	name: string | null;
	count: number;
}

/**
 * The request-list filters the API accepts. A `type`, not an `interface`: it is the TanStack
 * key (`queryKeys.applications.list` takes `Record<string, unknown>`), and only an alias gets
 * the implicit index signature that assignment needs.
 */
export type ApplicationListQuery = {
	associationId?: string;
	accountId?: string;
	status?: string;
	/** Only decisions logged by this user. */
	decidedBy?: string;
	/**
	 * `decided` = newest decision first; `account` = open first (newest filed), then decisions
	 * newest-decided — the account page's order, applied before the LIMIT. Default is newest
	 * request first.
	 */
	sort?: 'decided' | 'account';
	/** 1-based; `limit` is the page size. */
	page?: number;
	limit?: number;
};

class DataService {
	// Work Orders
	async getWorkOrders(options: QueryOptions = {}): Promise<PaginatedResponse<WorkOrder>> {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		logger.log('getWorkOrders', params.toString());
		const response = await apiService.fetchApi(`/work-orders?${params.toString()}`);

		// Transform the API response to match PaginatedResponse interface
		if (response && response.data && response.pagination) {
			return {
				data: response.data,
				total: response.pagination.total,
				page: response.pagination.page,
				limit: response.pagination.limit,
				hasNext: response.pagination.page < response.pagination.pages,
				hasPrev: response.pagination.page > 1,
			};
		}

		// Fallback for unexpected response structure
		return {
			data: response?.data || [],
			total: response?.pagination?.total || response?.total || 0,
			page: response?.pagination?.page || response?.page || 1,
			limit: response?.pagination?.limit || response?.limit || 50,
			hasNext: false,
			hasPrev: false,
		};
	}

	async getWorkOrder(id: string): Promise<WorkOrder> {
		return apiService.fetchApi(`/work-orders/${id}`);
	}

	async getWorkOrdersReport(params: WorkOrdersReportParams = {}): Promise<WorkOrdersReportResponse> {
		const now = new Date();
		const ninetyDaysAgo = new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));

		const merged: Required<Pick<WorkOrdersReportParams, 'status' | 'sortBy' | 'sortDirection'>> & WorkOrdersReportParams = {
			status: 'all',
			sortBy: 'orderDate',
			sortDirection: 'asc',
			startDate: params.startDate ?? ninetyDaysAgo,
			endDate: params.endDate ?? now,
			associationId: params.associationId ?? null,
			pmId: params.pmId ?? null,
			technicianId: params.technicianId ?? null,
			...params,
		};

		const toIso = (value: string | Date | null | undefined, isEndDate: boolean = false) => {
			if (value === null || value === undefined || value === '') return null;

			let date: Date;
			if (value instanceof Date) {
				date = new Date(value);
			} else {
				date = new Date(value);
			}

			// For end dates, set to end of day (23:59:59.999) to make them inclusive
			if (isEndDate) {
				date.setHours(23, 59, 59, 999);
			}

			return date.toISOString();
		};

		const searchParams = new URLSearchParams();

		const startDate = toIso(merged.startDate);
		const endDate = toIso(merged.endDate, true); // true = isEndDate
		const completedStartDate = toIso(merged.completedStartDate);
		const completedEndDate = toIso(merged.completedEndDate, true); // true = isEndDate
		const associationId = merged.associationId ?? undefined;
		const pmId = merged.pmId ?? undefined;
		const technicianId = merged.technicianId ?? undefined;

		if (startDate) searchParams.set('startDate', startDate);
		if (endDate) searchParams.set('endDate', endDate);
		if (completedStartDate) searchParams.set('completedStartDate', completedStartDate);
		if (completedEndDate) searchParams.set('completedEndDate', completedEndDate);
		searchParams.set('status', merged.status);
		if (associationId) searchParams.set('associationId', associationId);
		if (pmId) searchParams.set('pmId', pmId);
		if (technicianId) searchParams.set('technicianId', technicianId);
		searchParams.set('sortBy', merged.sortBy);
		searchParams.set('sortDirection', merged.sortDirection);

		const query = searchParams.toString();
		const path = query ? `/reports/work-orders?${query}` : '/reports/work-orders';

		return apiService.fetchApi(path);
	}

	async createWorkOrder(data: Partial<WorkOrder>): Promise<WorkOrder> {
		const response: Response = await apiService.fetchApi('/work-orders', {
			method: 'POST',
			body: JSON.stringify(data),
		}, null, false, true);

		if (!response.ok) {
			// Read once: a 409 that is NOT a duplicate (e.g. #417's termination block) used to read
			// the body as JSON and then again as text, which throws "body already used".
			const text = await response.text();
			let body: Record<string, unknown> | null = null;
			try {
				body = text.startsWith('{') ? JSON.parse(text) as Record<string, unknown> : null;
			} catch { /* not JSON — falls through to the raw-text error */ }
			if (response.status === 409 && body?.type === 'duplicate') {
				throw new DuplicateWorkOrderError(String(body.existingWorkOrderId), Number(body.existingOrderNumber));
			}
			if (body && typeof body.message === 'string') {
				throw new ApiResponseError(
					`API error ${response.status}: ${body.message}`,
					response.status,
					typeof body.code === 'string' ? body.code : undefined,
					body,
				);
			}
			throw new Error(`API error ${response.status}: ${text}`);
		}

		return response.json();
	}

	async updateWorkOrder(id: string, data: Partial<WorkOrder>): Promise<WorkOrder> {
		return apiService.fetchApi(`/work-orders/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteWorkOrder(id: string): Promise<void> {
		return apiService.fetchApi(`/work-orders/${id}`, {
			method: 'DELETE',
		});
	}

	/**
	 * Counts by status for the v2 dashboard tiles + donut (#279).
	 *
	 * One request, one GROUP BY. See the endpoint's note on why this is not five
	 * `?status=x&limit=1` calls.
	 */
	async getWorkOrderStats(params: { associationPmId?: string } = {}): Promise<{
		byStatus: Record<string, number>;
		/** OPEN work orders per property — the donut's slices. */
		byAssociation: Array<{ associationId: string | null; name: string; count: number }>;
		open: number;
		inProgress: number;
		completed: number;
		cancelled: number;
		quotes: number;
		pendingItems: number;
		pendingApproval: number;
		total: number;
	}> {
		const qs = params.associationPmId
			? `?associationPmId=${encodeURIComponent(params.associationPmId)}`
			: '';
		return apiService.fetchApi(`/work-orders/stats${qs}`);
	}

	// ── Review → approval transitions (#496) ────────────────────────────────────
	// Dedicated endpoints, not `updateWorkOrder` calls: each has side effects the
	// general-purpose PUT deliberately cannot trigger. See api/work-order-review.ts.

	/**
	 * Accounting is done reviewing; hand the work order to the property manager.
	 *
	 * Omit `customerDescription` to accept whatever is already stored — the server seeds it
	 * from the completion note when neither it nor the row has one.
	 */
	async markWorkOrderReadyForApproval(
		id: string,
		body: { customerDescription?: string } = {},
	): Promise<{
		id: string;
		orderNumber: number | null;
		customerDescription: string | null;
		reviewApprovedAt: number | null;
		reviewedBy: string | null;
		approvalDueAt: number;
		notifiedManagerId: string | null;
	}> {
		return apiService.fetchApi(`/work-orders/${id}/ready-for-approval`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	/** The PM disagrees with the customer-facing wording. `note` is required. */
	async returnWorkOrderForReview(id: string, note: string): Promise<{
		id: string;
		reviewApprovedAt: null;
		reviewReturnedAt: number | null;
		reviewReturnNote: string | null;
		notifiedReviewerId: string | null;
	}> {
		return apiService.fetchApi(`/work-orders/${id}/return-for-review`, {
			method: 'POST',
			body: JSON.stringify({ note }),
		});
	}

	/**
	 * Approve up to 25 work orders in one request.
	 *
	 * The cap is the server's; it TRUNCATES rather than rejecting and reports `truncated`,
	 * so a caller with more must loop. `approved` and `skipped` are counted apart — a
	 * re-driven chunk reports 0 approved rather than claiming work it did not do.
	 */
	async bulkApproveWorkOrders(workOrderIds: string[]): Promise<{
		processed: number;
		approved: number;
		approvedIds: string[];
		skipped: number;
		skippedIds: string[];
		errors: Array<{ workOrderId: string; error: string; code?: string }>;
		truncated: number;
		done: boolean;
	}> {
		return apiService.fetchApi('/work-orders/bulk-approve', {
			method: 'POST',
			body: JSON.stringify({ workOrderIds }),
		});
	}

	// Exterior Improvement Applications (#363)














	/** What applying an Owner Questionnaire's contacts WOULD do (preview, no writes). */
	/**
	 * Free-text account lookup for the "assign a lot" box (#369).
	 *
	 * Rides the GLOBAL search rather than a bespoke endpoint: it already matches an owner
	 * name, an address or an account number in one query and returns the association
	 * alongside, which is exactly the row a staffer needs to pick confidently. A scoped
	 * account search would have forced the association to be chosen first.
	 */
	async searchAccounts(query: string, opts: { associationId?: string } = {}): Promise<Array<{
		id: string; accountNumber?: string; address?: string;
		associationId?: string; associationName?: string; ownerNames?: string;
	}>> {
		if (query.trim().length < 2) return [];
		const params = new URLSearchParams({ q: query.trim(), types: 'account', limit: '25' });
		// Association scoping happens server-side, in SQL, so the endpoint's row limit
		// can't discard in-association lots before we see them (#438).
		if (opts.associationId) params.set('associationId', opts.associationId);
		// `data`, not `results` — the search endpoint's envelope. Reading the wrong key
		// returns an empty list for every query, which looks exactly like "no matches".
		const response = await apiService.fetchApi(`/search?${params.toString()}`);
		return ((response?.data ?? []) as any[])
			.filter((r) => r.type === 'account')
			.map((r) => ({
				id: r.id,
				accountNumber: r.metadata?.accountNumber ?? undefined,
				// `title` is "<address> #<number>" and the address carries a hard newline
				// from the stored value; flatten both for a single-line row.
				address: (r.title ?? '').replace(/\s*#\S+$/, '').replace(/\s*\n\s*/g, ', ').trim() || undefined,
				associationId: r.metadata?.associationId ?? undefined,
				associationName: r.metadata?.associationName ?? r.subtitle ?? undefined,
				ownerNames: r.metadata?.ownerNames ?? undefined,
			}));
	}

	/**
	 * Improvement requests (#369). The WORKFLOW on top of a message: status, review
	 * clock, board decision, checklist.
	 */
	async getApplications(query: ApplicationListQuery = {}) {
		const qs = new URLSearchParams();
		for (const [k, v] of Object.entries(query)) {
			if (v === undefined || v === null || v === '') continue;
			qs.set(k, String(v));
		}
		const response = await apiService.fetchApi(`/exterior-applications${qs.toString() ? `?${qs}` : ''}`);
		return { data: response?.data ?? [], pagination: response?.pagination ?? { total: 0, page: 1, pages: 1 } };
	}

	async getApplication(id: string): Promise<ExteriorApplicationDetail> {
		return apiService.fetchApi(`/exterior-applications/${id}`);
	}

	async getExteriorImprovementsReport(params: ExteriorImprovementsReportParams): Promise<ExteriorImprovementsReport> {
		return apiService.fetchApi(`/exterior-applications/report?${exteriorImprovementsReportQuery(params)}`, {
			timeoutMs: 60_000,
		});
	}

	/**
	 * Start an improvement request by hand — from an account page (spec §3b). The server
	 * refuses an association outside the caller's scope (404) and a lot that isn't in it (400).
	 */
	async createApplication(body: NewApplicationBody): Promise<ExteriorApplicationDetail> {
		return apiService.fetchApi('/exterior-applications', { method: 'POST', body: JSON.stringify(body) });
	}

	/** "This message is an improvement request." The message keeps its own inbox address. */
	async createApplicationFromMessage(body: { messageId: string; requestTypeId?: string; accountId?: string }) {
		return apiService.fetchApi('/exterior-applications/from-message', {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async updateApplication(id: string, body: Record<string, unknown>): Promise<ExteriorApplicationDetail> {
		return apiService.fetchApi(`/exterior-applications/${id}`, { method: 'PUT', body: JSON.stringify(body) });
	}

	/** Request types available to an association, for the type picker. */
	/**
	 * Add a request type the ruleset does not have yet, with its submission checklist.
	 *
	 * Admin-only server-side. The checklist is the point: a type with no requirements is a
	 * label, and the whole reason a reviewer wants "Driveway / Blacktop" to exist is so the
	 * next one arrives measured against the right list.
	 */
	async createRequestType(body: {
		name: string;
		slug: string;
		requiredAttachments: Array<{ key: string; label: string; required: boolean }>;
		reviewDeadlineDays?: number;
	}): Promise<{ id: string; name: string }> {
		return apiService.fetchApi('/application-rulesets/request-types', {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async getRequestTypes(associationId: string): Promise<Array<{ id: string; name: string }>> {
		const response = await apiService.fetchApi(`/exterior-applications/request-types?associationId=${encodeURIComponent(associationId)}`);
		return response?.data ?? [];
	}

	/**
	 * The inbox (#369). Every filter is a query param so a view is a URL — labels,
	 * a lot, a search, unread.
	 */
	async getMessages(query: MessageQuery = {}): Promise<MessageListPage> {
		const qs = new URLSearchParams();
		for (const [k, v] of Object.entries(query)) {
			if (v === undefined || v === null || v === false || v === '') continue;
			qs.set(k, v === true ? '1' : String(v));
		}
		const response = await apiService.fetchApi(`/messages${qs.toString() ? `?${qs}` : ''}`);
		return {
			data: response?.data ?? [],
			pagination: response?.pagination ?? { total: 0, page: 1, pages: 1 },
			...(typeof response?.elsewhere === 'number' ? { elsewhere: response.elsewhere } : {}),
			...(response?.everywhere ? { everywhere: true } : {}),
		};
	}

	/** One message, its thread, and the objects it turned into. */
	async getMessage(id: string): Promise<InboxMessageDetail> {
		return apiService.fetchApi(`/messages/${id}`);
	}

	async updateMessage(id: string, body: { read?: boolean; archived?: boolean; accountId?: string | null; associationId?: string | null; assignedTo?: string | null; formType?: string }): Promise<InboxMessage> {
		return apiService.fetchApi(`/messages/${id}`, { method: 'PUT', body: JSON.stringify(body) });
	}

	/**
	 * What the forward composer shows before anyone presses Send (#466) — the quoted
	 * original, the email gate, and where the attachments would be filed.
	 */
	async getForwardPreview(id: string): Promise<ForwardPreview> {
		return apiService.fetchApi(`/messages/${id}/forward`);
	}

	async forwardMessage(
		id: string,
		body: {
			to: string; note: string; includeAttachments: boolean;
			/** Files the sender ADDED — real attachments (#606); the message's own go as links. */
			attachments?: ReplyAttachmentRef[];
		},
	): Promise<ForwardResult> {
		// Uploading a handful of attachments to Dropbox happens inside this request, so the
		// default client timeout is not generous enough.
		return apiService.fetchApi(`/messages/${id}/forward`, {
			method: 'POST',
			body: JSON.stringify(body),
			timeoutMs: 120_000,
		});
	}

	/**
	 * What the reply composer shows before anyone presses Send (#532) — who we would be
	 * writing to, the email gate, and the canned replies on offer.
	 */
	async getReplyPreview(id: string): Promise<ReplyPreview> {
		return apiService.fetchApi(`/messages/${id}/reply`);
	}

	async replyToMessage(
		id: string,
		body: {
			/** Every recipient of the one email (#602). The route also accepts a comma-separated string. */
			to: string[]; subject: string; bodyText: string;
			ccSelf: boolean; archiveAfterSending: boolean;
			/** Quote the conversation under the message (#712). The server defaults it on. */
			includeHistory?: boolean;
			attachments?: ReplyAttachmentRef[];
		},
	): Promise<ReplyResult> {
		return apiService.fetchApi(`/messages/${id}/reply`, {
			method: 'POST',
			body: JSON.stringify(body),
			// Reading every attached file back out of storage and handing them to the provider
			// takes longer than a text-only send.
			...(body.attachments?.length ? { timeoutMs: 120_000 } : {}),
		});
	}

	/**
	 * Stage a file for any composer (#603, #606). Sends nothing. The browser's File on web; on
	 * native, the picked file's URI, which goes up as a bytes-backed part expo/fetch can encode.
	 */
	async stageComposeUpload(target: ComposeTarget, file: File | NativeFileSource): Promise<StagedReplyAttachment> {
		const formData = new FormData();
		if (file instanceof Blob) {
			formData.append('file', file);
		} else {
			appendNativeFile(formData, 'file', file);
		}
		return apiService.fetchApi(composeAttachmentsBase(target), {
			method: 'POST',
			body: formData,
			timeoutMs: 180_000,
		});
	}

	/** Copy one Dropbox file into a composer's staging (#606). Sends nothing. */
	async stageComposeDropbox(target: ComposeTarget, fileId: string): Promise<StagedReplyAttachment> {
		return apiService.fetchApi(`${composeAttachmentsBase(target)}/dropbox`, {
			method: 'POST',
			body: JSON.stringify({ fileId }),
			// A Dropbox download then an R2 write, for a file up to the email limit.
			timeoutMs: 120_000,
		});
	}

	/** Render a linked request's packet PDF and stage it. Same render budget as the board letter. */
	async stageReplyApplicationPdf(messageId: string, applicationId: string): Promise<StagedReplyAttachment> {
		return apiService.fetchApi(`/messages/${messageId}/reply/attachments/application-pdf`, {
			method: 'POST',
			body: JSON.stringify({ applicationId }),
			timeoutMs: 240_000,
		});
	}

	/**
	 * Open a file already stored as an attachment, on web OR iOS. The inbox's `openAttachment`
	 * falls back to a DOM download that does not exist on native; this goes through the same
	 * `openFetchedFile` as a staged file. No `await` before it — it reserves the web tab.
	 */
	async openAttachmentFile(attachmentId: string, fileName: string, mimeType: string): Promise<void> {
		return openFetchedFile(async () => {
			const response = await apiService.fetchApi(
				`/attachments/${attachmentId}/content`, { method: 'GET' }, undefined, false, true,
			);
			if (!(response instanceof Response)) throw new Error('Could not open that file');
			return response;
		}, fileName, mimeType);
	}

	/**
	 * Open a staged file with ITS OWN type — on iOS the type picks the apps the share sheet
	 * offers. No `await` before `openFetchedFile`, which reserves the web tab in the click.
	 */
	async openStagedComposeFile(target: ComposeTarget, token: string, fileName: string, mimeType: string): Promise<void> {
		return openFetchedFile(async () => {
			const response = await apiService.fetchApi(
				`${composeAttachmentsBase(target)}/${token}`, { method: 'GET' }, undefined, false, true,
			);
			if (!(response instanceof Response)) throw new Error('Could not open the attached file');
			return response;
		}, fileName, mimeType);
	}

	/** Inbound-routing rules (admin). */
	async getMessageRules(): Promise<{ data: MessageRuleRecord[] }> {
		return apiService.fetchApi('/message-rules');
	}

	async createMessageRule(body: {
		name: string;
		conditions?: Array<{ field: string; op: string; value: string }>;
		actions: RuleAction[];
		matchAll?: boolean;
		sortOrder?: number;
		enabled?: boolean;
	}): Promise<MessageRuleRecord> {
		return apiService.fetchApi('/message-rules', { method: 'POST', body: JSON.stringify(body) });
	}

	async updateMessageRule(
		id: string,
		body: Partial<{
			name: string;
			conditions: Array<{ field: string; op: string; value: string }>;
			actions: RuleAction[];
			matchAll: boolean;
			sortOrder: number;
			enabled: boolean;
		}>,
	): Promise<MessageRuleRecord> {
		return apiService.fetchApi(`/message-rules/${id}`, { method: 'PUT', body: JSON.stringify(body) });
	}

	async deleteMessageRule(id: string): Promise<{ success: boolean }> {
		return apiService.fetchApi(`/message-rules/${id}`, { method: 'DELETE' });
	}

	/**
	 * One CHUNK of a rule backfill. The caller loops until `done`.
	 *
	 * Deliberately not a "run the whole thing" method: the mandated bulk shape in this app is
	 * the client looping short bounded POSTs (CLAUDE.md), because every alternative tried —
	 * streaming, `ctx.waitUntil` + poll, fire-and-forget — failed under `wrangler dev`.
	 */
	async runMessageRuleChunk(
		id: string,
		body: { cursor?: string | null; chunkLimit?: number; dryRun?: boolean },
	): Promise<RuleRunChunk> {
		return apiService.fetchApi(`/message-rules/${id}/run`, {
			method: 'POST',
			body: JSON.stringify(body),
			timeoutMs: 60_000,
		});
	}

	/** File the attachments in Dropbox without emailing anyone. */
	async saveMessageToDropbox(id: string): Promise<SaveToDropboxResult> {
		return apiService.fetchApi(`/messages/${id}/save-to-dropbox`, { method: 'POST', timeoutMs: 120_000 });
	}

	/** Re-file a photo: its type, its caption, and which requirement it answers. */
	async updateAttachmentMetadata(
		id: string,
		body: { attachmentType?: string; description?: string | null; checklistKey?: string | null },
	) {
		return apiService.fetchApi(`/attachments/${id}`, { method: 'PUT', body: JSON.stringify(body) });
	}

	/** Nav-badge counts: how many requests are waiting on us, plus the per-stage breakdown. */
	async getApplicationCounts(): Promise<ApplicationCounts> {
		return apiService.fetchApi('/exterior-applications/counts');
	}

	/** Approved requests per approver — the Approved rail group (#611). */
	async getApplicationApprovers(): Promise<{ data: ApplicationApprover[] }> {
		return apiService.fetchApi('/exterior-applications/approvers');
	}

	/** Pull in website-form uploads this message links to but we don't hold. Idempotent. */
	async fetchMessageLinks(id: string): Promise<{ found: number; downloaded: number; alreadyHad: number; failed: Array<{ url: string; reason: string }> }> {
		return apiService.fetchApi(`/messages/${id}/fetch-links`, { method: 'POST', timeoutMs: 120_000 });
	}

	/** "This message is part of that request." Prevents a duplicate request. */
	async linkMessage(id: string, objectType: string, objectId: string) {
		return apiService.fetchApi(`/messages/${id}/link`, {
			method: 'POST',
			body: JSON.stringify({ objectType, objectId }),
		});
	}

	async unlinkMessage(id: string, objectType: string, objectId: string) {
		return apiService.fetchApi(`/messages/${id}/link/${objectType}/${objectId}`, { method: 'DELETE' });
	}

	/** Fold one improvement request into another; its messages move across. */
	async mergeApplication(id: string, intoId: string) {
		return apiService.fetchApi(`/exterior-applications/${id}/merge`, {
			method: 'POST',
			body: JSON.stringify({ intoId }),
		});
	}

	/**
	 * The label registry, with per-label totals and unread counts for the rail.
	 *
	 * `unfiledOnly` must match what the LIST is showing, or the rail advertises work the
	 * list refuses to display.
	 */
	async getLabels(unfiledOnly = false): Promise<LabelRegistry> {
		const response = await apiService.fetchApi(`/messages/labels${unfiledOnly ? '?unfiled=1' : ''}`);
		/**
		 * `statuses` falls back to the old `archived` field, so a client running against an
		 * API that predates migration 261 still renders an Archived count instead of a rail
		 * of zeroes. The two are read from one grouped query server-side and cannot disagree.
		 */
		const statuses = response?.statuses ?? {};
		return {
			labels: response?.data ?? [],
			needsHuman: Number(response?.needsHuman ?? 0),
			assignedToMe: Number(response?.assignedToMe ?? 0),
			statuses: {
				waiting: Number(statuses.waiting ?? 0),
				spam: Number(statuses.spam ?? 0),
				archived: Number(statuses.archived ?? response?.archived ?? 0),
			},
			archived: Number(statuses.archived ?? response?.archived ?? 0),
		};
	}

	/** Per-association counts for the grouped rail, in one request rather than one per node. */
	async getMessageGroups(unfiledOnly = false): Promise<InboxGroups> {
		const response = await apiService.fetchApi(`/messages/groups${unfiledOnly ? '?unfiled=1' : ''}`);
		return {
			unassociated: response?.unassociated ?? { total: 0, unread: 0, needsHuman: 0 },
			groups: response?.groups ?? [],
		};
	}

	async createLabel(body: { name: string; color?: string; icon?: string }): Promise<InboxLabel> {
		return apiService.fetchApi('/messages/labels', { method: 'POST', body: JSON.stringify(body) });
	}

	async addMessageLabel(id: string, slug: string): Promise<void> {
		await apiService.fetchApi(`/messages/${id}/labels`, { method: 'POST', body: JSON.stringify({ slug }) });
	}

	async removeMessageLabel(id: string, slug: string): Promise<void> {
		await apiService.fetchApi(`/messages/${id}/labels/${slug}`, { method: 'DELETE' });
	}


	async getMessageContactPlan(id: string): Promise<ContactPlan> {
		return apiService.fetchApi(`/messages/${id}/contacts`);
	}

	/** Actually create/update the contacts. Re-plans server-side. */
	async applyMessageContacts(id: string): Promise<ApplyContactsResult> {
		return apiService.fetchApi(`/messages/${id}/contacts`, { method: 'POST', body: '{}' });
	}

	/** Maintenance Request → a work order. Staff-initiated, never automatic. */
	async createWorkOrderFromMessage(
		id: string,
		body: { priority?: string; workRequested?: string; terminationAck?: boolean } = {},
	): Promise<{ workOrderId: string; orderNumber: number | null }> {
		return apiService.fetchApi(`/messages/${id}/work-order`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}





	/** The reverse of reclassify: promote a submission to a real improvement request. */
	async convertFormSubmission(
		id: string,
		body: { requestTypeId: string; associationId?: string; accountId?: string },
	): Promise<{ applicationId: string; accountId: string | null }> {
		return apiService.fetchApi(`/form-submissions/${id}/convert`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}


	// Exterior-application email intake (unknown-sender triage)




	// Application ruleset admin — shared request types + checklists + letters (#363, Phase 5)
	async getApplicationRuleset(): Promise<ApplicationRuleset> {
		return apiService.fetchApi('/application-rulesets');
	}

	async getEmailTemplates(kind?: string): Promise<EmailTemplate[]> {
		const response = await apiService.fetchApi(`/email-templates${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`);
		return response?.data ?? [];
	}

	async getNeedsInfoEmailPreview(applicationId: string): Promise<NeedsInfoEmailPreview> {
		return apiService.fetchApi(`/exterior-applications/${applicationId}/needs-info-email`);
	}

	async sendNeedsInfoEmail(applicationId: string, body: NeedsInfoSendBody): Promise<NeedsInfoSendResult> {
		return apiService.fetchApi(`/exterior-applications/${applicationId}/needs-info-email`, {
			method: 'POST',
			body: JSON.stringify(body),
			// Same reason as Reply: every attached file is read back out of storage and handed to
			// the provider inside this request. A client that gives up at 30s while the Worker
			// still sends leaves the outcome unknown — and invites a second letter.
			...(body.attachments?.length ? { timeoutMs: 120_000 } : {}),
		});
	}

	async getBoardApprovalEmailPreview(applicationId: string): Promise<BoardApprovalEmailPreview> {
		return apiService.fetchApi(`/exterior-applications/${applicationId}/board-approval-email`);
	}

	/**
	 * Render the application to PDF and stage it for the composer. Sends nothing. Longer than
	 * the renderer's own worst case (two 90s waits plus launch).
	 */
	async stageBoardApprovalAttachment(applicationId: string): Promise<StagedBoardApprovalAttachment> {
		return apiService.fetchApi(`/exterior-applications/${applicationId}/board-approval-email/attachment`, {
			method: 'POST',
			timeoutMs: 240_000,
		});
	}

	/**
	 * Open the staged PDF — exactly the file Send will attach. No `await` before
	 * `openFetchedPdf`: on web it must reserve the tab inside the click's user gesture.
	 */
	async openBoardApprovalAttachment(applicationId: string, token: string, fileName: string): Promise<void> {
		return openFetchedPdf(async () => {
			const response = await apiService.fetchApi(
				`/exterior-applications/${applicationId}/board-approval-email/attachment/${token}`,
				{ method: 'GET' }, undefined, false, true,
			);
			if (!(response instanceof Response)) throw new Error('Could not open the attached PDF');
			return response;
		}, fileName);
	}

	async sendBoardApprovalEmail(applicationId: string, body: BoardApprovalSendBody): Promise<BoardApprovalSendResult> {
		return apiService.fetchApi(`/exterior-applications/${applicationId}/board-approval-email`, {
			method: 'POST',
			body: JSON.stringify(body),
			// ALWAYS carries files — the packet, plus documents and additions (#608, #606) — so it
			// always gets the long timeout, for the reason given on `sendNeedsInfoEmail`.
			timeoutMs: 120_000,
		});
	}

	/**
	 * Register a file staff uploaded against a request. The bytes must already be in R2 via
	 * `uploadFile` — this is the second half, which creates the row and mirrors it to the
	 * document store.
	 */
	async addApplicationAttachment(
		applicationId: string, body: ApplicationAttachmentUpload,
	): Promise<AddedApplicationAttachment> {
		return apiService.fetchApi(`/exterior-applications/${applicationId}/attachments`, {
			method: 'POST',
			body: JSON.stringify(body),
		});
	}

	async createEmailTemplate(data: Partial<EmailTemplate>): Promise<EmailTemplate> {
		return apiService.fetchApi('/email-templates', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateEmailTemplate(id: string, data: Partial<EmailTemplate>): Promise<EmailTemplate> {
		return apiService.fetchApi(`/email-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) });
	}

	async deleteEmailTemplate(id: string): Promise<void> {
		await apiService.fetchApi(`/email-templates/${id}`, { method: 'DELETE' });
	}

	/** Includes DISABLED rows — this serves the editor, not the composer. */
	async getInboxReplyTemplates(): Promise<InboxReplyTemplate[]> {
		const response = await apiService.fetchApi('/inbox-reply-templates');
		return response?.data ?? [];
	}

	async createInboxReplyTemplate(data: Partial<InboxReplyTemplate>): Promise<InboxReplyTemplate> {
		return apiService.fetchApi('/inbox-reply-templates', { method: 'POST', body: JSON.stringify(data) });
	}

	async updateInboxReplyTemplate(id: string, data: Partial<InboxReplyTemplate>): Promise<InboxReplyTemplate> {
		return apiService.fetchApi(`/inbox-reply-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) });
	}

	async deleteInboxReplyTemplate(id: string): Promise<void> {
		await apiService.fetchApi(`/inbox-reply-templates/${id}`, { method: 'DELETE' });
	}

	async createApplicationRequestType(data: Partial<ApplicationRequestType>): Promise<ApplicationRequestType> {
		return apiService.fetchApi('/application-rulesets/request-types', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateApplicationRequestType(id: string, data: Partial<ApplicationRequestType>): Promise<ApplicationRequestType> {
		return apiService.fetchApi(`/application-rulesets/request-types/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteApplicationRequestType(id: string): Promise<void> {
		return apiService.fetchApi(`/application-rulesets/request-types/${id}`, {
			method: 'DELETE',
		});
	}

	// Associations
	async getAssociations(options: QueryOptions = {}): Promise<PaginatedResponse<Association>> {
		try {
			const params = new URLSearchParams();
			for (const [key, value] of Object.entries(options)) {
				if (value !== undefined && value !== null) {
					params.append(key, String(value));
				}
			}
			const response = await apiService.fetchApi(`/associations?${params.toString()}`);

			// Transform the API response to match PaginatedResponse interface
			if (response && response.data && response.pagination) {
				return {
					data: response.data,
					total: response.pagination.total,
					page: response.pagination.page,
					limit: response.pagination.limit,
					hasNext: response.pagination.page < response.pagination.pages,
					hasPrev: response.pagination.page > 1,
				};
			}

			// Fallback for unexpected response structure
			return {
				data: response?.data || [],
				total: response?.pagination?.total || response?.total || 0,
				page: response?.pagination?.page || response?.page || 1,
				limit: response?.pagination?.limit || response?.limit || 10,
				hasNext: false,
				hasPrev: false,
			};
		} catch (error) {
			console.error('getAssociations error:', error);
			// Return empty result instead of throwing to prevent crashes
			return {
				data: [],
				total: 0,
				page: 1,
				limit: 10,
				hasNext: false,
				hasPrev: false,
			};
		}
	}

	async getAssociation(id: string): Promise<Association> {
		return apiService.fetchApi(`/associations/${id}`);
	}

	async createAssociation(data: Partial<Association>): Promise<Association> {
		return apiService.fetchApi('/associations', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateAssociation(id: string, data: Partial<Association>): Promise<Association> {
		return apiService.fetchApi(`/associations/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteAssociation(id: string): Promise<void> {
		return apiService.fetchApi(`/associations/${id}`, {
			method: 'DELETE',
		});
	}

	// Association Shortcut Codes
	async getAssociationShortcutCodes(options: QueryOptions = {}): Promise<PaginatedResponse<AssociationShortcutCode>> {
		try {
			const params = new URLSearchParams();
			for (const [key, value] of Object.entries(options)) {
				if (value !== undefined && value !== null) {
					params.append(key, String(value));
				}
			}
			const response = await apiService.fetchApi(`/association-shortcut-codes?${params.toString()}`);

			// Transform the API response to match PaginatedResponse interface
			if (response && response.data && response.pagination) {
				return {
					data: response.data,
					total: response.pagination.total,
					page: response.pagination.page,
					limit: response.pagination.limit,
					hasNext: response.pagination.page < response.pagination.pages,
					hasPrev: response.pagination.page > 1,
				};
			}

			// Fallback for unexpected response structure
			return {
				data: response?.data || [],
				total: response?.pagination?.total || response?.total || 0,
				page: response?.pagination?.page || response?.page || 1,
				limit: response?.pagination?.limit || response?.limit || 50,
				hasNext: false,
				hasPrev: false,
			};
		} catch (error) {
			console.error('getAssociationShortcutCodes error:', error);
			// Return empty result instead of throwing to prevent crashes
			return {
				data: [],
				total: 0,
				page: 1,
				limit: 50,
				hasNext: false,
				hasPrev: false,
			};
		}
	}

	async createShortcutCode(data: Partial<AssociationShortcutCode>): Promise<AssociationShortcutCode> {
		return apiService.fetchApi('/association-shortcut-codes', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateShortcutCode(id: string, data: Partial<AssociationShortcutCode>): Promise<AssociationShortcutCode> {
		return apiService.fetchApi(`/association-shortcut-codes/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteShortcutCode(id: string): Promise<void> {
		return apiService.fetchApi(`/association-shortcut-codes/${id}`, {
			method: 'DELETE',
		});
	}

	async searchShortcutCodes(query: string, associationId?: string): Promise<AssociationShortcutCode[]> {
		const params = new URLSearchParams();
		params.append('search', query);
		if (associationId) {
			params.append('associationId', associationId);
		}
		const response = await apiService.fetchApi(`/association-shortcut-codes?${params.toString()}`);
		return response?.data || [];
	}

	// Users
	async getUsers(options: QueryOptions = {}): Promise<PaginatedResponse<User>> {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		return apiService.fetchApi(`/users?${params.toString()}`);
	}

	async getUser(id: string): Promise<User> {
		return apiService.fetchApi(`/users/${id}`);
	}

	async createUser(data: Partial<User>): Promise<User> {
		return apiService.fetchApi('/users', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateUser(id: string, data: Partial<User>): Promise<User> {
		return apiService.fetchApi(`/users/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteUser(id: string): Promise<void> {
		return apiService.fetchApi(`/users/${id}`, {
			method: 'DELETE',
		});
	}

	// Account (self)
	async deleteAccount(): Promise<void> {
		return apiService.fetchApi('/account', {
			method: 'DELETE',
		});
	}

	// Time Entries
	async getTimeEntries(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<TimeEntry>> {
		const params = new URLSearchParams();
		if (workOrderId) {
			params.append('workOrderId', workOrderId);
		}
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		return apiService.fetchApi(`/time-entries?${params.toString()}`);
	}

	async getTimeEntry(id: string): Promise<TimeEntry> {
		return apiService.fetchApi(`/time-entries/${id}`);
	}

	async createTimeEntry(data: Partial<TimeEntry>): Promise<TimeEntry> {
		logger.log('createTimeEntry:', data);
		return apiService.fetchApi('/time-entries', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateTimeEntry(id: string, data: Partial<TimeEntry>): Promise<TimeEntry> {
		return apiService.fetchApi(`/time-entries/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteTimeEntry(id: string): Promise<void> {
		return apiService.fetchApi(`/time-entries/${id}`, {
			method: 'DELETE',
		});
	}

	// Materials
	async getMaterials(options: QueryOptions = {}): Promise<PaginatedResponse<Material>> {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		return apiService.fetchApi(`/materials?${params.toString()}`);
	}

	async getMaterial(id: string): Promise<Material> {
		return apiService.fetchApi(`/materials/${id}`);
	}

	async createMaterial(data: Partial<Material>): Promise<Material> {
		return apiService.fetchApi('/materials', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateMaterial(id: string, data: Partial<Material>): Promise<Material> {
		return apiService.fetchApi(`/materials/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteMaterial(id: string): Promise<void> {
		return apiService.fetchApi(`/materials/${id}`, {
			method: 'DELETE',
		});
	}

	// Material Costs
	async getMaterialCosts(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<MaterialCost>> {
		const params = new URLSearchParams();
		if (workOrderId) {
			params.append('workOrderId', workOrderId);
		}
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		return apiService.fetchApi(`/material-costs?${params.toString()}`);
	}

	async getMaterialCost(id: string): Promise<MaterialCost> {
		return apiService.fetchApi(`/material-costs/${id}`);
	}

	async createMaterialCost(data: Partial<MaterialCost>): Promise<MaterialCost> {
		return apiService.fetchApi('/material-costs', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateMaterialCost(id: string, data: Partial<MaterialCost>): Promise<MaterialCost> {
		return apiService.fetchApi(`/material-costs/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteMaterialCost(id: string): Promise<void> {
		return apiService.fetchApi(`/material-costs/${id}`, {
			method: 'DELETE',
		});
	}

	// Misc Costs
	async getMiscCosts(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<MiscCost>> {
		const params = new URLSearchParams();
		if (workOrderId) {
			params.append('workOrderId', workOrderId);
		}
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		return apiService.fetchApi(`/misc-costs?${params.toString()}`);
	}

	async getMiscCost(id: string): Promise<MiscCost> {
		return apiService.fetchApi(`/misc-costs/${id}`);
	}

	async createMiscCost(data: Partial<MiscCost>): Promise<MiscCost> {
		return apiService.fetchApi('/misc-costs', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateMiscCost(id: string, data: Partial<MiscCost>): Promise<MiscCost> {
		return apiService.fetchApi(`/misc-costs/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteMiscCost(id: string): Promise<void> {
		return apiService.fetchApi(`/misc-costs/${id}`, {
			method: 'DELETE',
		});
	}

	// Notes
	async getNotes(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<Note>> {
		const params = new URLSearchParams();
		if (workOrderId) {
			params.append('workOrderId', workOrderId);
		}
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		return apiService.fetchApi(`/notes?${params.toString()}`);
	}

	async getNote(id: string): Promise<Note> {
		return apiService.fetchApi(`/notes/${id}`);
	}

	async createNote(data: Partial<Note>): Promise<Note> {
		return apiService.fetchApi('/notes', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateNote(id: string, data: Partial<Note>): Promise<Note> {
		return apiService.fetchApi(`/notes/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteNote(id: string): Promise<void> {
		return apiService.fetchApi(`/notes/${id}`, {
			method: 'DELETE',
		});
	}

	// Attachments
	async getAttachments(workOrderId?: string, inspectionReportId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<Attachment>> {
		const params = new URLSearchParams();
		if (workOrderId) {
			params.append('workOrderId', workOrderId);
		}
		if (inspectionReportId) {
			params.append('inspectionReportId', inspectionReportId);
		}
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		const response = await apiService.fetchApi(`/attachments?${params.toString()}`);
		if (response?.data) {
			response.data = response.data.map((a: any) => normalizeAttachment(a));
		}
		return response;
	}

	async getAttachment(id: string): Promise<Attachment> {
		const a = await apiService.fetchApi(`/attachments/${id}`);
		return normalizeAttachment(a);
	}

	async createAttachment(data: Partial<Attachment>): Promise<Attachment> {
		const a = await apiService.fetchApi('/attachments', {
			method: 'POST',
			body: JSON.stringify(data),
		});
		return normalizeAttachment(a);
	}

	async updateAttachment(id: string, data: Partial<Attachment>): Promise<Attachment> {
		const a = await apiService.fetchApi(`/attachments/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
		return normalizeAttachment(a);
	}

	async deleteAttachment(id: string): Promise<void> {
		return apiService.fetchApi(`/attachments/${id}`, {
			method: 'DELETE',
		});
	}

	// Convenience methods for getting attachments by specific entities
	async getAttachmentsByWorkOrder(workOrderId: string): Promise<Attachment[]> {
		const response = await this.getAttachments(workOrderId);
		return response.data.map(a => normalizeAttachment(a));
	}

	async getAttachmentsByAssociation(associationId: string): Promise<Attachment[]> {
		const params = new URLSearchParams();
		params.append('associationId', associationId);
		const response = await apiService.fetchApi(`/attachments?${params.toString()}`);
		return (response.data || []).map((a: any) => normalizeAttachment(a));
	}

	async getAttachmentsByInspectionReport(inspectionReportId: string): Promise<Attachment[]> {
		const params = new URLSearchParams();
		params.append('inspectionReportId', inspectionReportId);
		const response = await apiService.fetchApi(`/attachments?${params.toString()}`);
		return (response.data || []).map((a: any) => normalizeAttachment(a));
	}

	async getAttachmentsByBill(billId: string): Promise<Attachment[]> {
		const params = new URLSearchParams();
		params.append('billId', billId);
		const response = await apiService.fetchApi(`/attachments?${params.toString()}`);
		return (response.data || []).map((a: any) => normalizeAttachment(a));
	}

	async getAttachmentsByInvoice(invoiceId: string): Promise<Attachment[]> {
		const params = new URLSearchParams();
		params.append('invoiceId', invoiceId);
		const response = await apiService.fetchApi(`/attachments?${params.toString()}`);
		return (response.data || []).map((a: any) => normalizeAttachment(a));
	}

	async getAttachmentsByPayment(paymentId: string): Promise<Attachment[]> {
		const params = new URLSearchParams();
		params.append('paymentId', paymentId);
		const response = await apiService.fetchApi(`/attachments?${params.toString()}`);
		return (response.data || []).map((a: any) => normalizeAttachment(a));
	}

	async getAttachmentsByApplication(applicationId: string): Promise<Attachment[]> {
		const params = new URLSearchParams();
		params.append('applicationId', applicationId);
		const response = await apiService.fetchApi(`/attachments?${params.toString()}`);
		return (response.data || []).map((a: any) => normalizeAttachment(a));
	}

	// Location Events
	async getLocationEvents(userId?: string, workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<LocationEvent>> {
		const params = new URLSearchParams();
		if (userId) {
			params.append('userId', userId);
		}
		if (workOrderId) {
			params.append('workOrderId', workOrderId);
		}
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		const res = await apiService.fetchApi(`/location-events?${params.toString()}`);
		return {
			...res,
			data: (res.data || []).map((e: Record<string, unknown>) =>
				normalizeLocationEventFromApi(e) as unknown as LocationEvent,
			),
		};
	}

	async getLocationEvent(id: string): Promise<LocationEvent> {
		const e = await apiService.fetchApi(`/location-events/${id}`);
		return normalizeLocationEventFromApi(e) as unknown as LocationEvent;
	}

	async createLocationEvent(data: Partial<LocationEvent>): Promise<LocationEvent> {
		const raw = (data.type ?? data.eventType) as string | undefined;
		const eventType
			= raw === 'check_out' || raw === 'manual_check_out' || raw === 'checkout'
				? 'checkout'
				: 'checkin';
		const eventTimestamp
			= typeof data.eventTimestamp === 'number'
				? data.eventTimestamp
				: data.timestamp
					? new Date(data.timestamp).getTime()
					: Date.now();
		const userLatitude = data.userLatitude ?? data.lat ?? (data as Record<string, unknown>).latitude;
		const userLongitude = data.userLongitude ?? data.lng ?? (data as Record<string, unknown>).longitude;
		const body = {
			eventType,
			eventTimestamp,
			userLatitude,
			userLongitude,
			associationId: data.associationId,
			userId: data.userId,
			workOrderId: data.workOrderId,
			distanceMeters: (data as Record<string, unknown>).distanceMeters,
			targetLatitude: (data as Record<string, unknown>).targetLatitude,
			targetLongitude: (data as Record<string, unknown>).targetLongitude,
		};
		const created = await apiService.fetchApi('/location-events', {
			method: 'POST',
			body: JSON.stringify(body),
		});
		return normalizeLocationEventFromApi(created as Record<string, unknown>) as unknown as LocationEvent;
	}

	async updateLocationEvent(id: string, data: Partial<LocationEvent>): Promise<LocationEvent> {
		return apiService.fetchApi(`/location-events/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteLocationEvent(id: string): Promise<void> {
		return apiService.fetchApi(`/location-events/${id}`, {
			method: 'DELETE',
		});
	}

	// Location analytics
	async getLocationAnalytics(options: QueryOptions = {}): Promise<PaginatedResponse<LocationAnalyticsEntry>> {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}

		const response = await apiService.fetchApi(`/analytics?${params.toString()}`);

		const data: LocationAnalyticsEntry[] = (response?.data || []).map((item: any) => ({
			id: item.id,
			userId: item.userId,
			userName: item.userName,
			userEmail: item.userEmail,
			action: item.action,
			latitude: item.latitude ?? null,
			longitude: item.longitude ?? null,
			accuracy: item.accuracy ?? null,
			appVersion: item.appVersion ?? null,
			devicePlatform: item.devicePlatform ?? null,
			deviceOS: item.deviceOS ?? null,
			deviceModel: item.deviceModel ?? null,
			ipAddress: item.ipAddress ?? null,
			userAgent: item.userAgent ?? null,
			createdAt: item.createdAt,
			rawPayload: item.payload ?? null,
		}));

		const pagination = response?.pagination || {};
		return {
			data,
			total: pagination.total ?? data.length,
			page: pagination.page ?? 1,
			limit: pagination.limit ?? data.length,
			hasNext: pagination.pages ? (pagination.page ?? 1) < pagination.pages : false,
			hasPrev: pagination.page ? pagination.page > 1 : false,
		};
	}

	// Inspection Reports
	async getInspectionReports(associationId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<InspectionReport>> {
		const params = new URLSearchParams();
		if (associationId) {
			params.append('associationId', associationId);
		}
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		return apiService.fetchApi(`/inspection-reports?${params.toString()}`);
	}

	async getInspectionReport(id: string): Promise<InspectionReport> {
		return apiService.fetchApi(`/inspection-reports/${id}`);
	}

	async createInspectionReport(data: Partial<InspectionReport>): Promise<InspectionReport> {
		return apiService.fetchApi('/inspection-reports', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateInspectionReport(id: string, data: Partial<InspectionReport>): Promise<InspectionReport> {
		return apiService.fetchApi(`/inspection-reports/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteInspectionReport(id: string): Promise<void> {
		return apiService.fetchApi(`/inspection-reports/${id}`, {
			method: 'DELETE',
		});
	}

	// Inspection Issues
	async getInspectionIssues(options: QueryOptions = {}): Promise<PaginatedResponse<InspectionIssue>> {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		return apiService.fetchApi(`/inspection-issues?${params.toString()}`);
	}

	async getInspectionIssue(id: string): Promise<InspectionIssue> {
		return apiService.fetchApi(`/inspection-issues/${id}`);
	}

	async createInspectionIssue(data: Partial<InspectionIssue>): Promise<InspectionIssue> {
		return apiService.fetchApi('/inspection-issues', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateInspectionIssue(id: string, data: Partial<InspectionIssue>): Promise<InspectionIssue> {
		return apiService.fetchApi(`/inspection-issues/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteInspectionIssue(id: string): Promise<void> {
		return apiService.fetchApi(`/inspection-issues/${id}`, {
			method: 'DELETE',
		});
	}

	// Inspection Report Issues
	async getInspectionReportIssues(inspectionReportId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<InspectionReportIssue>> {
		const params = new URLSearchParams();
		if (inspectionReportId) {
			params.append('inspectionReportId', inspectionReportId);
		}
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		return apiService.fetchApi(`/inspection-report-issues?${params.toString()}`);
	}

	async getInspectionReportIssue(id: string): Promise<InspectionReportIssue> {
		return apiService.fetchApi(`/inspection-report-issues/${id}`);
	}

	async createInspectionReportIssue(data: Partial<InspectionReportIssue>): Promise<InspectionReportIssue> {
		return apiService.fetchApi('/inspection-report-issues', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateInspectionReportIssue(id: string, data: Partial<InspectionReportIssue>): Promise<InspectionReportIssue> {
		return apiService.fetchApi(`/inspection-report-issues/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteInspectionReportIssue(id: string): Promise<void> {
		return apiService.fetchApi(`/inspection-report-issues/${id}`, {
			method: 'DELETE',
		});
	}

	// Feature Flags
	async getMyFeatureFlags(): Promise<Record<string, boolean>> {
		return apiService.fetchApi('/me/feature-flags');
	}

	async getFeatureFlags(options: QueryOptions = {}): Promise<PaginatedResponse<FeatureFlag>> {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		const response = await apiService.fetchApi(`/admin/feature-flags?${params.toString()}`);
		if (response && response.data && response.pagination) {
			return {
				data: response.data,
				total: response.pagination.total,
				page: response.pagination.page,
				limit: response.pagination.limit,
				hasNext: response.pagination.page < response.pagination.pages,
				hasPrev: response.pagination.page > 1,
			};
		}
		return {
			data: response?.data || [],
			total: response?.pagination?.total || 0,
			page: response?.pagination?.page || 1,
			limit: response?.pagination?.limit || 50,
			hasNext: false,
			hasPrev: false,
		};
	}

	async setFeatureFlag(userId: string, flagName: string, enabled: boolean): Promise<FeatureFlag> {
		return apiService.fetchApi(`/admin/feature-flags/${userId}/${flagName}`, {
			method: 'PUT',
			body: JSON.stringify({ enabled }),
		});
	}

	// Tasks
	async getTasks(options: QueryOptions = {}): Promise<PaginatedResponse<Task>> {
		const params = new URLSearchParams();
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		const response = await apiService.fetchApi(`/tasks?${params.toString()}`);
		if (response && response.data && response.pagination) {
			return {
				data: response.data,
				total: response.pagination.total,
				page: response.pagination.page,
				limit: response.pagination.limit,
				hasNext: response.pagination.page < response.pagination.pages,
				hasPrev: response.pagination.page > 1,
			};
		}
		return {
			data: response?.data || [],
			total: response?.pagination?.total || 0,
			page: response?.pagination?.page || 1,
			limit: response?.pagination?.limit || 50,
			hasNext: false,
			hasPrev: false,
		};
	}

	async getTask(id: string): Promise<Task> {
		return apiService.fetchApi(`/tasks/${id}`);
	}

	async createTask(data: Partial<Task>): Promise<Task> {
		return apiService.fetchApi('/tasks', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async updateTask(id: string, data: Partial<Task>): Promise<Task> {
		return apiService.fetchApi(`/tasks/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async deleteTask(id: string): Promise<void> {
		return apiService.fetchApi(`/tasks/${id}`, {
			method: 'DELETE',
		});
	}

	// Follow-ups (#48) — ungated by the tasks_enabled feature flag.
	// Pass workOrderId to scope to a single work order (all assignees); omit it
	// for the current user's dashboard feed.
	async getFollowUps(workOrderId?: string): Promise<FollowUp[]> {
		const path = workOrderId
			? `/work-orders/follow-ups?workOrderId=${encodeURIComponent(workOrderId)}`
			: '/work-orders/follow-ups';
		const response = await apiService.fetchApi(path);
		return response?.data || [];
	}

	async addFollowUp(workOrderId: string, note?: string): Promise<{ taskId: string; workOrderId: string }> {
		return apiService.fetchApi('/work-orders/follow-ups', {
			method: 'POST',
			body: JSON.stringify({ workOrderId, note }),
		});
	}

	async resolveFollowUp(taskId: string): Promise<void> {
		return apiService.fetchApi(`/work-orders/follow-ups/${taskId}`, {
			method: 'PUT',
		});
	}

	// Calendar Sources & Events
	async getCalendarSources(): Promise<CalendarSource[]> {
		const response = await apiService.fetchApi('/calendars');
		return response?.data || [];
	}

	async getCalendarEvents(timeMin: number, timeMax: number): Promise<CalendarEvent[]> {
		const response = await apiService.fetchApi(`/calendars/events?timeMin=${timeMin}&timeMax=${timeMax}`);
		return response?.data || [];
	}

	async updateCalendarSource(id: string, data: Partial<CalendarSource>): Promise<void> {
		await apiService.fetchApi(`/calendars/${id}`, {
			method: 'PATCH',
			body: JSON.stringify(data),
		});
	}

	async deleteCalendarSource(sourceId: string): Promise<void> {
		await apiService.fetchApi(`/calendars/sources/${sourceId}`, {
			method: 'DELETE',
		});
	}

	async disconnectCalendar(connectionId: string): Promise<void> {
		await apiService.fetchApi(`/calendars/connections/${connectionId}`, {
			method: 'DELETE',
		});
	}

	async syncCalendars(): Promise<void> {
		await apiService.fetchApi('/calendars/sync', {
			method: 'POST',
		});
	}

	// Batch operations
	async batchUpdate<T>(endpoint: string, updates: Array<{ id: string; data: Partial<T> }>): Promise<T[]> {
		return apiService.fetchApi(`/${endpoint}/batch`, {
			method: 'PUT',
			body: JSON.stringify(updates),
		});
	}

	async batchDelete(endpoint: string, ids: string[]): Promise<void> {
		return apiService.fetchApi(`/${endpoint}/batch`, {
			method: 'DELETE',
			body: JSON.stringify({ ids }),
		});
	}

	// Global Search
	async globalSearch(query: string, options: QueryOptions = {}): Promise<PaginatedResponse<any>> {
		const params = new URLSearchParams();
		params.append('q', query);
		for (const [key, value] of Object.entries(options)) {
			if (value !== undefined && value !== null) {
				params.append(key, String(value));
			}
		}
		return apiService.fetchApi(`/search?${params.toString()}`);
	}

	// History
	async getHistory(filters: {
		objectType?: string;
		objectId?: string;
		property?: string;
		userId?: string;
		transactionId?: string;
		since?: number;
		until?: number;
		page?: number;
		limit?: number;
	} = {}): Promise<{ data: any[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
		const params = new URLSearchParams();
		for (const [k, v] of Object.entries(filters)) {
			if (v !== undefined && v !== null && v !== '') params.append(k.replace(/[A-Z]/g, m => '_' + m.toLowerCase()), String(v));
		}
		return apiService.fetchApi(`/history?${params.toString()}`);
	}

	async getHistoryStats(filters: {
		since?: number;
		until?: number;
		objectType?: string;
	} = {}): Promise<{ byUser: any[]; byObjectType: any[]; byDay: any[]; byUserDay: any[] }> {
		const params = new URLSearchParams();
		for (const [k, v] of Object.entries(filters)) {
			if (v !== undefined && v !== null && v !== '') params.append(k.replace(/[A-Z]/g, m => '_' + m.toLowerCase()), String(v));
		}
		return apiService.fetchApi(`/history/stats?${params.toString()}`);
	}

	/**
	 * Send a push notification to all devices for a specific user
	 */
	async sendNotificationToUser(
		userId: string,
		title: string,
		body: string,
		data?: {
			type?: 'update' | 'workorder' | 'general';
			workOrderId?: string;
			action?: 'testflight' | 'workorder' | 'modal';
			url?: string;
			[key: string]: any;
		},
	): Promise<any> {
		return apiService.fetchApi('/notifications/send', {
			method: 'POST',
			body: JSON.stringify({
				userId,
				title,
				body,
				data,
			}),
		});
	}
}

class RoutedDataService {
	private remote: DataService;

	constructor(remote: DataService) {
		this.remote = remote;
	}

	private async useDemoMode(): Promise<boolean> {
		const session = await authService.getSession();
		return !!session?.isDemoMode;
	}

	// Work Orders
	async getWorkOrders(options: QueryOptions = {}): Promise<PaginatedResponse<WorkOrder>> {
		if (await this.useDemoMode()) return demoDataService.getWorkOrders(options);
		return this.remote.getWorkOrders(options);
	}

	async getWorkOrder(id: string): Promise<WorkOrder> {
		if (await this.useDemoMode()) return demoDataService.getWorkOrder(id);
		return this.remote.getWorkOrder(id);
	}

	async getWorkOrdersReport(params: WorkOrdersReportParams = {}): Promise<WorkOrdersReportResponse> {
		if (await this.useDemoMode()) return demoDataService.getWorkOrdersReport(params);
		return this.remote.getWorkOrdersReport(params);
	}

	async createWorkOrder(data: Partial<WorkOrder>): Promise<WorkOrder> {
		if (await this.useDemoMode()) return demoDataService.createWorkOrder(data);
		return this.remote.createWorkOrder(data);
	}

	async updateWorkOrder(id: string, data: Partial<WorkOrder>): Promise<WorkOrder> {
		if (await this.useDemoMode()) return demoDataService.updateWorkOrder(id, data);
		return this.remote.updateWorkOrder(id, data);
	}

	async deleteWorkOrder(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteWorkOrder(id);
		return this.remote.deleteWorkOrder(id);
	}

	async getWorkOrderStats(params: { associationPmId?: string } = {}) {
		return this.remote.getWorkOrderStats(params);
	}

	/**
	 * Review → approval transitions (#496) — always remote, no demo fixtures.
	 *
	 * Demo mode has no notion of these states, and a stub that silently "succeeded" would
	 * be worse than a failure: the whole feature is a gate, and a demo where the gate
	 * always opens teaches the wrong thing about it.
	 */
	async markWorkOrderReadyForApproval(id: string, body: { customerDescription?: string } = {}) {
		return this.remote.markWorkOrderReadyForApproval(id, body);
	}

	async returnWorkOrderForReview(id: string, note: string) {
		return this.remote.returnWorkOrderForReview(id, note);
	}

	async bulkApproveWorkOrders(workOrderIds: string[]) {
		return this.remote.bulkApproveWorkOrders(workOrderIds);
	}

	// Exterior Improvement Applications (#363) — no demo-mode fixtures; always remote.















	async searchAccounts(query: string, opts: { associationId?: string } = {}) {
		return this.remote.searchAccounts(query, opts);
	}

	async getApplications(query: ApplicationListQuery = {}) {
		return this.remote.getApplications(query);
	}

	async getApplication(id: string): Promise<ExteriorApplicationDetail> {
		return this.remote.getApplication(id);
	}

	async getExteriorImprovementsReport(params: ExteriorImprovementsReportParams): Promise<ExteriorImprovementsReport> {
		return this.remote.getExteriorImprovementsReport(params);
	}

	async createApplication(body: NewApplicationBody): Promise<ExteriorApplicationDetail> {
		return this.remote.createApplication(body);
	}

	async createApplicationFromMessage(body: { messageId: string; requestTypeId?: string; accountId?: string }) {
		return this.remote.createApplicationFromMessage(body);
	}

	async updateApplication(id: string, body: Record<string, unknown>): Promise<ExteriorApplicationDetail> {
		return this.remote.updateApplication(id, body);
	}

	async createRequestType(body: {
		name: string;
		slug: string;
		requiredAttachments: Array<{ key: string; label: string; required: boolean }>;
		reviewDeadlineDays?: number;
	}) {
		return this.remote.createRequestType(body);
	}

	async getRequestTypes(associationId: string): Promise<Array<{ id: string; name: string }>> {
		return this.remote.getRequestTypes(associationId);
	}

	async getMessages(query: MessageQuery = {}): Promise<MessageListPage> {
		return this.remote.getMessages(query);
	}

	async fetchMessageLinks(id: string) {
		return this.remote.fetchMessageLinks(id);
	}

	async linkMessage(id: string, objectType: string, objectId: string) {
		return this.remote.linkMessage(id, objectType, objectId);
	}

	async unlinkMessage(id: string, objectType: string, objectId: string) {
		return this.remote.unlinkMessage(id, objectType, objectId);
	}

	async mergeApplication(id: string, intoId: string) {
		return this.remote.mergeApplication(id, intoId);
	}

	async getApplicationCounts() {
		return this.remote.getApplicationCounts();
	}

	async getApplicationApprovers() {
		return this.remote.getApplicationApprovers();
	}

	async updateAttachmentMetadata(
		id: string,
		body: { attachmentType?: string; description?: string | null; checklistKey?: string | null },
	) {
		return this.remote.updateAttachmentMetadata(id, body);
	}

	async getMessage(id: string): Promise<InboxMessageDetail> {
		return this.remote.getMessage(id);
	}

	async updateMessage(id: string, body: { read?: boolean; archived?: boolean; accountId?: string | null; associationId?: string | null; assignedTo?: string | null; formType?: string }): Promise<InboxMessage> {
		return this.remote.updateMessage(id, body);
	}

	async getForwardPreview(id: string): Promise<ForwardPreview> {
		return this.remote.getForwardPreview(id);
	}

	async forwardMessage(
		id: string,
		body: { to: string; note: string; includeAttachments: boolean; attachments?: ReplyAttachmentRef[] },
	): Promise<ForwardResult> {
		return this.remote.forwardMessage(id, body);
	}

	async saveMessageToDropbox(id: string): Promise<SaveToDropboxResult> {
		return this.remote.saveMessageToDropbox(id);
	}

	async getReplyPreview(id: string): Promise<ReplyPreview> {
		return this.remote.getReplyPreview(id);
	}

	async replyToMessage(
		id: string,
		body: {
			to: string[]; subject: string; bodyText: string; ccSelf: boolean; archiveAfterSending: boolean;
			includeHistory?: boolean;
			attachments?: ReplyAttachmentRef[];
		},
	): Promise<ReplyResult> {
		return this.remote.replyToMessage(id, body);
	}

	async stageComposeUpload(target: ComposeTarget, file: File | NativeFileSource): Promise<StagedReplyAttachment> {
		return this.remote.stageComposeUpload(target, file);
	}

	async stageComposeDropbox(target: ComposeTarget, fileId: string): Promise<StagedReplyAttachment> {
		return this.remote.stageComposeDropbox(target, fileId);
	}

	async stageReplyApplicationPdf(messageId: string, applicationId: string): Promise<StagedReplyAttachment> {
		return this.remote.stageReplyApplicationPdf(messageId, applicationId);
	}

	async openStagedComposeFile(target: ComposeTarget, token: string, fileName: string, mimeType: string): Promise<void> {
		return this.remote.openStagedComposeFile(target, token, fileName, mimeType);
	}

	async openAttachmentFile(attachmentId: string, fileName: string, mimeType: string): Promise<void> {
		return this.remote.openAttachmentFile(attachmentId, fileName, mimeType);
	}

	async getMessageRules(): Promise<{ data: MessageRuleRecord[] }> {
		return this.remote.getMessageRules();
	}

	async createMessageRule(body: {
		name: string;
		conditions?: Array<{ field: string; op: string; value: string }>;
		actions: RuleAction[];
		matchAll?: boolean;
		sortOrder?: number;
		enabled?: boolean;
	}): Promise<MessageRuleRecord> {
		return this.remote.createMessageRule(body);
	}

	async updateMessageRule(
		id: string,
		body: Partial<{
			name: string;
			conditions: Array<{ field: string; op: string; value: string }>;
			actions: RuleAction[];
			matchAll: boolean;
			sortOrder: number;
			enabled: boolean;
		}>,
	): Promise<MessageRuleRecord> {
		return this.remote.updateMessageRule(id, body);
	}

	async deleteMessageRule(id: string): Promise<{ success: boolean }> {
		return this.remote.deleteMessageRule(id);
	}

	async runMessageRuleChunk(
		id: string,
		body: { cursor?: string | null; chunkLimit?: number; dryRun?: boolean },
	): Promise<RuleRunChunk> {
		return this.remote.runMessageRuleChunk(id, body);
	}

	async getLabels(unfiledOnly = false): Promise<LabelRegistry> {
		return this.remote.getLabels(unfiledOnly);
	}

	async getMessageGroups(unfiledOnly = false): Promise<InboxGroups> {
		return this.remote.getMessageGroups(unfiledOnly);
	}

	async createLabel(body: { name: string; color?: string; icon?: string }): Promise<InboxLabel> {
		return this.remote.createLabel(body);
	}

	async addMessageLabel(id: string, slug: string): Promise<void> {
		return this.remote.addMessageLabel(id, slug);
	}

	async removeMessageLabel(id: string, slug: string): Promise<void> {
		return this.remote.removeMessageLabel(id, slug);
	}

	async getMessageContactPlan(id: string): Promise<ContactPlan> {
		return this.remote.getMessageContactPlan(id);
	}

	async applyMessageContacts(id: string): Promise<ApplyContactsResult> {
		return this.remote.applyMessageContacts(id);
	}

	async createWorkOrderFromMessage(
		id: string,
		body: { priority?: string; workRequested?: string; terminationAck?: boolean } = {},
	): Promise<{ workOrderId: string; orderNumber: number | null }> {
		return this.remote.createWorkOrderFromMessage(id, body);
	}



	async convertFormSubmission(
		id: string,
		body: { requestTypeId: string; associationId?: string; accountId?: string },
	): Promise<{ applicationId: string; accountId: string | null }> {
		return this.remote.convertFormSubmission(id, body);
	}








	// Application ruleset admin (#363, Phase 5)
	async getApplicationRuleset(): Promise<ApplicationRuleset> {
		return this.remote.getApplicationRuleset();
	}

	async getEmailTemplates(kind?: string): Promise<EmailTemplate[]> {
		return this.remote.getEmailTemplates(kind);
	}

	async getNeedsInfoEmailPreview(applicationId: string): Promise<NeedsInfoEmailPreview> {
		return this.remote.getNeedsInfoEmailPreview(applicationId);
	}

	async addApplicationAttachment(
		applicationId: string, body: ApplicationAttachmentUpload,
	): Promise<AddedApplicationAttachment> {
		return this.remote.addApplicationAttachment(applicationId, body);
	}

	async sendNeedsInfoEmail(applicationId: string, body: NeedsInfoSendBody): Promise<NeedsInfoSendResult> {
		return this.remote.sendNeedsInfoEmail(applicationId, body);
	}

	async getBoardApprovalEmailPreview(applicationId: string): Promise<BoardApprovalEmailPreview> {
		return this.remote.getBoardApprovalEmailPreview(applicationId);
	}

	async stageBoardApprovalAttachment(applicationId: string): Promise<StagedBoardApprovalAttachment> {
		return this.remote.stageBoardApprovalAttachment(applicationId);
	}

	async openBoardApprovalAttachment(applicationId: string, token: string, fileName: string): Promise<void> {
		return this.remote.openBoardApprovalAttachment(applicationId, token, fileName);
	}

	async sendBoardApprovalEmail(applicationId: string, body: BoardApprovalSendBody): Promise<BoardApprovalSendResult> {
		return this.remote.sendBoardApprovalEmail(applicationId, body);
	}

	async createEmailTemplate(data: Partial<EmailTemplate>): Promise<EmailTemplate> {
		return this.remote.createEmailTemplate(data);
	}

	async updateEmailTemplate(id: string, data: Partial<EmailTemplate>): Promise<EmailTemplate> {
		return this.remote.updateEmailTemplate(id, data);
	}

	async deleteEmailTemplate(id: string): Promise<void> {
		return this.remote.deleteEmailTemplate(id);
	}

	async getInboxReplyTemplates(): Promise<InboxReplyTemplate[]> {
		return this.remote.getInboxReplyTemplates();
	}

	async createInboxReplyTemplate(data: Partial<InboxReplyTemplate>): Promise<InboxReplyTemplate> {
		return this.remote.createInboxReplyTemplate(data);
	}

	async updateInboxReplyTemplate(id: string, data: Partial<InboxReplyTemplate>): Promise<InboxReplyTemplate> {
		return this.remote.updateInboxReplyTemplate(id, data);
	}

	async deleteInboxReplyTemplate(id: string): Promise<void> {
		return this.remote.deleteInboxReplyTemplate(id);
	}

	async createApplicationRequestType(data: Partial<ApplicationRequestType>): Promise<ApplicationRequestType> {
		return this.remote.createApplicationRequestType(data);
	}

	async updateApplicationRequestType(id: string, data: Partial<ApplicationRequestType>): Promise<ApplicationRequestType> {
		return this.remote.updateApplicationRequestType(id, data);
	}

	async deleteApplicationRequestType(id: string): Promise<void> {
		return this.remote.deleteApplicationRequestType(id);
	}

	// Associations
	async getAssociations(options: QueryOptions = {}): Promise<PaginatedResponse<Association>> {
		if (await this.useDemoMode()) return demoDataService.getAssociations(options);
		return this.remote.getAssociations(options);
	}

	async getAssociation(id: string): Promise<Association> {
		if (await this.useDemoMode()) return demoDataService.getAssociation(id);
		return this.remote.getAssociation(id);
	}

	async createAssociation(data: Partial<Association>): Promise<Association> {
		if (await this.useDemoMode()) return demoDataService.createAssociation(data);
		return this.remote.createAssociation(data);
	}

	async updateAssociation(id: string, data: Partial<Association>): Promise<Association> {
		if (await this.useDemoMode()) return demoDataService.updateAssociation(id, data);
		return this.remote.updateAssociation(id, data);
	}

	async deleteAssociation(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteAssociation(id);
		return this.remote.deleteAssociation(id);
	}

	// Association Shortcut Codes
	async getAssociationShortcutCodes(options: QueryOptions = {}): Promise<PaginatedResponse<AssociationShortcutCode>> {
		if (await this.useDemoMode()) return demoDataService.getAssociationShortcutCodes(options);
		return this.remote.getAssociationShortcutCodes(options);
	}

	async createShortcutCode(data: Partial<AssociationShortcutCode>): Promise<AssociationShortcutCode> {
		if (await this.useDemoMode()) return demoDataService.createShortcutCode(data);
		return this.remote.createShortcutCode(data);
	}

	async updateShortcutCode(id: string, data: Partial<AssociationShortcutCode>): Promise<AssociationShortcutCode> {
		if (await this.useDemoMode()) return demoDataService.updateShortcutCode(id, data);
		return this.remote.updateShortcutCode(id, data);
	}

	async deleteShortcutCode(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteShortcutCode(id);
		return this.remote.deleteShortcutCode(id);
	}

	async searchShortcutCodes(query: string, associationId?: string): Promise<AssociationShortcutCode[]> {
		if (await this.useDemoMode()) return demoDataService.searchShortcutCodes(query, associationId);
		return this.remote.searchShortcutCodes(query, associationId);
	}

	// Users
	async getUsers(options: QueryOptions = {}): Promise<PaginatedResponse<User>> {
		if (await this.useDemoMode()) return demoDataService.getUsers(options);
		return this.remote.getUsers(options);
	}

	async getUser(id: string): Promise<User> {
		if (await this.useDemoMode()) return demoDataService.getUser(id);
		return this.remote.getUser(id);
	}

	async createUser(data: Partial<User>): Promise<User> {
		if (await this.useDemoMode()) return demoDataService.createUser(data);
		return this.remote.createUser(data);
	}

	async updateUser(id: string, data: Partial<User>): Promise<User> {
		if (await this.useDemoMode()) return demoDataService.updateUser(id, data);
		return this.remote.updateUser(id, data);
	}

	async deleteUser(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteUser(id);
		return this.remote.deleteUser(id);
	}

	// Account
	async deleteAccount(): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteAccount();
		return this.remote.deleteAccount();
	}

	// Time Entries
	async getTimeEntries(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<TimeEntry>> {
		if (await this.useDemoMode()) return demoDataService.getTimeEntries(workOrderId, options);
		return this.remote.getTimeEntries(workOrderId, options);
	}

	async getTimeEntry(id: string): Promise<TimeEntry> {
		if (await this.useDemoMode()) return demoDataService.getTimeEntry(id);
		return this.remote.getTimeEntry(id);
	}

	async createTimeEntry(data: Partial<TimeEntry>): Promise<TimeEntry> {
		if (await this.useDemoMode()) return demoDataService.createTimeEntry(data);
		return this.remote.createTimeEntry(data);
	}

	async updateTimeEntry(id: string, data: Partial<TimeEntry>): Promise<TimeEntry> {
		if (await this.useDemoMode()) return demoDataService.updateTimeEntry(id, data);
		return this.remote.updateTimeEntry(id, data);
	}

	async deleteTimeEntry(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteTimeEntry(id);
		return this.remote.deleteTimeEntry(id);
	}

	// Materials
	async getMaterials(options: QueryOptions = {}): Promise<PaginatedResponse<Material>> {
		if (await this.useDemoMode()) return demoDataService.getMaterials(options);
		return this.remote.getMaterials(options);
	}

	async getMaterial(id: string): Promise<Material> {
		if (await this.useDemoMode()) return demoDataService.getMaterial(id);
		return this.remote.getMaterial(id);
	}

	async createMaterial(data: Partial<Material>): Promise<Material> {
		if (await this.useDemoMode()) return demoDataService.createMaterial(data);
		return this.remote.createMaterial(data);
	}

	async updateMaterial(id: string, data: Partial<Material>): Promise<Material> {
		if (await this.useDemoMode()) return demoDataService.updateMaterial(id, data);
		return this.remote.updateMaterial(id, data);
	}

	async deleteMaterial(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteMaterial(id);
		return this.remote.deleteMaterial(id);
	}

	// Material Costs
	async getMaterialCosts(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<MaterialCost>> {
		if (await this.useDemoMode()) return demoDataService.getMaterialCosts(workOrderId, options);
		return this.remote.getMaterialCosts(workOrderId, options);
	}

	async getMaterialCost(id: string): Promise<MaterialCost> {
		if (await this.useDemoMode()) return demoDataService.getMaterialCost(id);
		return this.remote.getMaterialCost(id);
	}

	async createMaterialCost(data: Partial<MaterialCost>): Promise<MaterialCost> {
		if (await this.useDemoMode()) return demoDataService.createMaterialCost(data);
		return this.remote.createMaterialCost(data);
	}

	async updateMaterialCost(id: string, data: Partial<MaterialCost>): Promise<MaterialCost> {
		if (await this.useDemoMode()) return demoDataService.updateMaterialCost(id, data);
		return this.remote.updateMaterialCost(id, data);
	}

	async deleteMaterialCost(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteMaterialCost(id);
		return this.remote.deleteMaterialCost(id);
	}

	// Misc Costs
	async getMiscCosts(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<MiscCost>> {
		if (await this.useDemoMode()) return demoDataService.getMiscCosts(workOrderId, options);
		return this.remote.getMiscCosts(workOrderId, options);
	}

	async getMiscCost(id: string): Promise<MiscCost> {
		if (await this.useDemoMode()) return demoDataService.getMiscCost(id);
		return this.remote.getMiscCost(id);
	}

	async createMiscCost(data: Partial<MiscCost>): Promise<MiscCost> {
		if (await this.useDemoMode()) return demoDataService.createMiscCost(data);
		return this.remote.createMiscCost(data);
	}

	async updateMiscCost(id: string, data: Partial<MiscCost>): Promise<MiscCost> {
		if (await this.useDemoMode()) return demoDataService.updateMiscCost(id, data);
		return this.remote.updateMiscCost(id, data);
	}

	async deleteMiscCost(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteMiscCost(id);
		return this.remote.deleteMiscCost(id);
	}

	// Notes
	async getNotes(workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<Note>> {
		if (await this.useDemoMode()) return demoDataService.getNotes(workOrderId, options);
		return this.remote.getNotes(workOrderId, options);
	}

	async getNote(id: string): Promise<Note> {
		if (await this.useDemoMode()) return demoDataService.getNote(id);
		return this.remote.getNote(id);
	}

	async createNote(data: Partial<Note>): Promise<Note> {
		if (await this.useDemoMode()) return demoDataService.createNote(data);
		return this.remote.createNote(data);
	}

	async updateNote(id: string, data: Partial<Note>): Promise<Note> {
		if (await this.useDemoMode()) return demoDataService.updateNote(id, data);
		return this.remote.updateNote(id, data);
	}

	async deleteNote(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteNote(id);
		return this.remote.deleteNote(id);
	}

	// Attachments
	async getAttachments(workOrderId?: string, inspectionReportId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<Attachment>> {
		if (await this.useDemoMode()) return demoDataService.getAttachments(workOrderId, inspectionReportId, options);
		return this.remote.getAttachments(workOrderId, inspectionReportId, options);
	}

	async getAttachment(id: string): Promise<Attachment> {
		if (await this.useDemoMode()) return demoDataService.getAttachment(id);
		return this.remote.getAttachment(id);
	}

	async createAttachment(data: Partial<Attachment>): Promise<Attachment> {
		if (await this.useDemoMode()) return demoDataService.createAttachment(data);
		return this.remote.createAttachment(data);
	}

	async updateAttachment(id: string, data: Partial<Attachment>): Promise<Attachment> {
		if (await this.useDemoMode()) return demoDataService.updateAttachment(id, data);
		return this.remote.updateAttachment(id, data);
	}

	async deleteAttachment(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteAttachment(id);
		return this.remote.deleteAttachment(id);
	}

	async getAttachmentsByWorkOrder(workOrderId: string): Promise<Attachment[]> {
		if (await this.useDemoMode()) return demoDataService.getAttachmentsByWorkOrder(workOrderId);
		return this.remote.getAttachmentsByWorkOrder(workOrderId);
	}

	async getAttachmentsByAssociation(associationId: string): Promise<Attachment[]> {
		if (await this.useDemoMode()) return demoDataService.getAttachmentsByAssociation(associationId);
		return this.remote.getAttachmentsByAssociation(associationId);
	}

	async getAttachmentsByInspectionReport(inspectionReportId: string): Promise<Attachment[]> {
		if (await this.useDemoMode()) return demoDataService.getAttachmentsByInspectionReport(inspectionReportId);
		return this.remote.getAttachmentsByInspectionReport(inspectionReportId);
	}

	async getAttachmentsByBill(billId: string): Promise<Attachment[]> {
		if (await this.useDemoMode()) return [];
		return this.remote.getAttachmentsByBill(billId);
	}

	async getAttachmentsByInvoice(invoiceId: string): Promise<Attachment[]> {
		if (await this.useDemoMode()) return [];
		return this.remote.getAttachmentsByInvoice(invoiceId);
	}

	async getAttachmentsByPayment(paymentId: string): Promise<Attachment[]> {
		if (await this.useDemoMode()) return [];
		return this.remote.getAttachmentsByPayment(paymentId);
	}

	async getAttachmentsByApplication(applicationId: string): Promise<Attachment[]> {
		if (await this.useDemoMode()) return [];
		return this.remote.getAttachmentsByApplication(applicationId);
	}

	// Location Events
	async getLocationEvents(userId?: string, workOrderId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<LocationEvent>> {
		if (await this.useDemoMode()) return demoDataService.getLocationEvents(userId, workOrderId, options);
		return this.remote.getLocationEvents(userId, workOrderId, options);
	}

	async getLocationEvent(id: string): Promise<LocationEvent> {
		if (await this.useDemoMode()) return demoDataService.getLocationEvent(id);
		return this.remote.getLocationEvent(id);
	}

	async createLocationEvent(data: Partial<LocationEvent>): Promise<LocationEvent> {
		if (await this.useDemoMode()) return demoDataService.createLocationEvent(data);
		return this.remote.createLocationEvent(data);
	}

	async updateLocationEvent(id: string, data: Partial<LocationEvent>): Promise<LocationEvent> {
		if (await this.useDemoMode()) return demoDataService.updateLocationEvent(id, data);
		return this.remote.updateLocationEvent(id, data);
	}

	async deleteLocationEvent(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteLocationEvent(id);
		return this.remote.deleteLocationEvent(id);
	}

	async getLocationAnalytics(options: QueryOptions = {}): Promise<PaginatedResponse<LocationAnalyticsEntry>> {
		if (await this.useDemoMode()) return demoDataService.getLocationAnalytics(options);
		return this.remote.getLocationAnalytics(options);
	}

	// Inspection Reports
	async getInspectionReports(associationId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<InspectionReport>> {
		if (await this.useDemoMode()) return demoDataService.getInspectionReports(associationId, options);
		return this.remote.getInspectionReports(associationId, options);
	}

	async getInspectionReport(id: string): Promise<InspectionReport> {
		if (await this.useDemoMode()) return demoDataService.getInspectionReport(id);
		return this.remote.getInspectionReport(id);
	}

	async createInspectionReport(data: Partial<InspectionReport>): Promise<InspectionReport> {
		if (await this.useDemoMode()) return demoDataService.createInspectionReport(data);
		return this.remote.createInspectionReport(data);
	}

	async updateInspectionReport(id: string, data: Partial<InspectionReport>): Promise<InspectionReport> {
		if (await this.useDemoMode()) return demoDataService.updateInspectionReport(id, data);
		return this.remote.updateInspectionReport(id, data);
	}

	async deleteInspectionReport(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteInspectionReport(id);
		return this.remote.deleteInspectionReport(id);
	}

	// Inspection Issues
	async getInspectionIssues(options: QueryOptions = {}): Promise<PaginatedResponse<InspectionIssue>> {
		if (await this.useDemoMode()) return demoDataService.getInspectionIssues(options);
		return this.remote.getInspectionIssues(options);
	}

	async getInspectionIssue(id: string): Promise<InspectionIssue> {
		if (await this.useDemoMode()) return demoDataService.getInspectionIssue(id);
		return this.remote.getInspectionIssue(id);
	}

	async createInspectionIssue(data: Partial<InspectionIssue>): Promise<InspectionIssue> {
		if (await this.useDemoMode()) return demoDataService.createInspectionIssue(data);
		return this.remote.createInspectionIssue(data);
	}

	async updateInspectionIssue(id: string, data: Partial<InspectionIssue>): Promise<InspectionIssue> {
		if (await this.useDemoMode()) return demoDataService.updateInspectionIssue(id, data);
		return this.remote.updateInspectionIssue(id, data);
	}

	async deleteInspectionIssue(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteInspectionIssue(id);
		return this.remote.deleteInspectionIssue(id);
	}

	// Inspection Report Issues
	async getInspectionReportIssues(inspectionReportId?: string, options: QueryOptions = {}): Promise<PaginatedResponse<InspectionReportIssue>> {
		if (await this.useDemoMode()) return demoDataService.getInspectionReportIssues(inspectionReportId, options);
		return this.remote.getInspectionReportIssues(inspectionReportId, options);
	}

	async getInspectionReportIssue(id: string): Promise<InspectionReportIssue> {
		if (await this.useDemoMode()) return demoDataService.getInspectionReportIssue(id);
		return this.remote.getInspectionReportIssue(id);
	}

	async createInspectionReportIssue(data: Partial<InspectionReportIssue>): Promise<InspectionReportIssue> {
		if (await this.useDemoMode()) return demoDataService.createInspectionReportIssue(data);
		return this.remote.createInspectionReportIssue(data);
	}

	async updateInspectionReportIssue(id: string, data: Partial<InspectionReportIssue>): Promise<InspectionReportIssue> {
		if (await this.useDemoMode()) return demoDataService.updateInspectionReportIssue(id, data);
		return this.remote.updateInspectionReportIssue(id, data);
	}

	async deleteInspectionReportIssue(id: string): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.deleteInspectionReportIssue(id);
		return this.remote.deleteInspectionReportIssue(id);
	}

	// Feature Flags
	async getMyFeatureFlags(): Promise<Record<string, boolean>> {
		return this.remote.getMyFeatureFlags();
	}

	async getFeatureFlags(options: QueryOptions = {}): Promise<PaginatedResponse<FeatureFlag>> {
		return this.remote.getFeatureFlags(options);
	}

	async setFeatureFlag(userId: string, flagName: string, enabled: boolean): Promise<FeatureFlag> {
		return this.remote.setFeatureFlag(userId, flagName, enabled);
	}

	// Tasks
	async getTasks(options: QueryOptions = {}): Promise<PaginatedResponse<Task>> {
		return this.remote.getTasks(options);
	}

	async getTask(id: string): Promise<Task> {
		return this.remote.getTask(id);
	}

	async createTask(data: Partial<Task>): Promise<Task> {
		return this.remote.createTask(data);
	}

	async updateTask(id: string, data: Partial<Task>): Promise<Task> {
		return this.remote.updateTask(id, data);
	}

	async deleteTask(id: string): Promise<void> {
		return this.remote.deleteTask(id);
	}

	// Follow-ups (#48)
	async getFollowUps(workOrderId?: string): Promise<FollowUp[]> {
		return this.remote.getFollowUps(workOrderId);
	}

	async addFollowUp(workOrderId: string, note?: string): Promise<{ taskId: string; workOrderId: string }> {
		return this.remote.addFollowUp(workOrderId, note);
	}

	async resolveFollowUp(taskId: string): Promise<void> {
		return this.remote.resolveFollowUp(taskId);
	}

	// Calendar Sources & Events
	async getCalendarSources(): Promise<CalendarSource[]> {
		return this.remote.getCalendarSources();
	}

	async getCalendarEvents(timeMin: number, timeMax: number): Promise<CalendarEvent[]> {
		return this.remote.getCalendarEvents(timeMin, timeMax);
	}

	async updateCalendarSource(id: string, data: Partial<CalendarSource>): Promise<void> {
		return this.remote.updateCalendarSource(id, data);
	}

	async deleteCalendarSource(sourceId: string): Promise<void> {
		return this.remote.deleteCalendarSource(sourceId);
	}

	async disconnectCalendar(connectionId: string): Promise<void> {
		return this.remote.disconnectCalendar(connectionId);
	}

	async syncCalendars(): Promise<void> {
		return this.remote.syncCalendars();
	}

	// Batch operations
	async batchUpdate<T>(endpoint: string, updates: Array<{ id: string; data: Partial<T> }>): Promise<T[]> {
		if (await this.useDemoMode()) return demoDataService.batchUpdate(endpoint, updates);
		return this.remote.batchUpdate(endpoint, updates);
	}

	async batchDelete(endpoint: string, ids: string[]): Promise<void> {
		if (await this.useDemoMode()) return demoDataService.batchDelete(endpoint, ids);
		return this.remote.batchDelete(endpoint, ids);
	}

	// Global Search
	async globalSearch(query: string, options: QueryOptions = {}): Promise<PaginatedResponse<any>> {
		if (await this.useDemoMode()) return demoDataService.globalSearch(query, options);
		return this.remote.globalSearch(query, options);
	}

	// History
	async getHistory(filters: {
		objectType?: string;
		objectId?: string;
		property?: string;
		userId?: string;
		transactionId?: string;
		since?: number;
		until?: number;
		page?: number;
		limit?: number;
	} = {}): Promise<{ data: any[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
		if (await this.useDemoMode()) {
			void filters;
			return demoDataService.getHistory();
		}
		return this.remote.getHistory(filters);
	}

	async getHistoryStats(filters: {
		since?: number;
		until?: number;
		objectType?: string;
	} = {}): Promise<{ byUser: any[]; byObjectType: any[]; byDay: any[]; byUserDay: any[] }> {
		return this.remote.getHistoryStats(filters);
	}

	// Notifications
	async sendNotificationToUser(
		userId: string,
		title: string,
		body: string,
		data?: {
			type?: 'update' | 'workorder' | 'general';
			workOrderId?: string;
			action?: 'testflight' | 'workorder' | 'modal';
			url?: string;
			[key: string]: any;
		},
	): Promise<any> {
		if (await this.useDemoMode()) return demoDataService.sendNotificationToUser(userId, title, body, data);
		return this.remote.sendNotificationToUser(userId, title, body, data);
	}
}

export const dataService = new RoutedDataService(new DataService());
